import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { CookieService } from 'ngx-cookie';
import { HelperService } from 'app/services/helper.service';
import { environment } from 'environments/environment';
import { IUpdateOrderAuthorizationData, IUpdateOrderDetailsData, IUpdateOrderStatusData, IUpdateOrderWorkflowData } from './order';
import { getPayrixInfo, getQboPaymentMethods, laborRegularList, laborSummaryList, labourSummaryStats, PaymentMethodsResponse, terminalRes } from './sharedInterfaces';
import { DataService } from './data.service';
import { IDriver } from 'app/modules/app-components/driver-side-list/driver-side-list.interface';
// import { prepareEventListenerParameters } from '@angular/compiler/src/render3/view/template';

interface CustomerSearch {
  limit?: number;
  populateVehicles?: boolean;
  populateContact?: boolean;
  populatePricing?: boolean;
  populateTaxCodeOverRide?: boolean;
}
interface UpdateOnlineOrderInvRefPayload {
  poId: string;
  onlineItem: {
    code?: string;
    onlineLineId?: string;
    onlinePartId?: string;
    orderItemId: string;
  };
  inventoryItem: {
    itemId: string;
    title: string;
    partNumber: string;
  };
}
@Injectable()
export class HTTPService {
  private token;
  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private helperService: HelperService,
    private router: Router,
    private dataService: DataService,
  ) { }

  private getHeaders() {
    this.token = this.cookieService.getObject('alpat');
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      authorization: this.token ? this.token : null,
    });
    return this.headers;
  }

  private getFileHeaders() {
    this.token = this.cookieService.getObject('alpat');
    this.headers = new HttpHeaders({
      authorization: this.token ? this.token : null,
    });
    return this.headers;
  }

  private getSiteHeaders(token) {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      authorization: token,
    });
    return this.headers;
  }

  private setHeaderToken(token) {
    return (this.headers = new HttpHeaders({
      authorization: token,
    }));
  }

  updateEstimateAttachment(attachmentId: string, data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/attachment/${attachmentId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSession(token: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/updateSession`, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          authorization: token,
        }),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  login(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/login`, this.helperService.encryptData(data), { headers: this.headers })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  siteLogin(query: any, token: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.partnerapi_base_url}/auth/siteLogin`, query);

    return this.http
      .get(url, {
        headers: this.getSiteHeaders(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  forgotPassword(email: string, payload: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/forgetpassword/${email}`, payload, {
        headers: this.headers,
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  recordAuthActivityLogs(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/authActivityLogs`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  resetPassword(token: any, payload: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/resetpassword/${token}`, this.helperService.encryptData(payload), { headers: this.headers })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setDefaultShop(data: any, token: string): Promise<any> {
    return this.http
      .put(`${environment.partnerapi_base_url}/users/defaultShop`, data, {
        headers: this.getSiteHeaders(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getUserACL(token): Promise<any> {
    return this.http
      .get(`${environment.partnerapi_base_url}/users/acl`, { headers: this.getSiteHeaders(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setNotificationPlayerId(playerId: string) {
    return this.http
      .put(`${environment.api_base_url}/employees/playerId`, { playerId }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createCustomer(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/customers`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSubletSupplier(data: any, orderId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimate/sublet/assignSupplier/${orderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSubletItems(orderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimate/sublet/subletsOrder/${orderId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createDriver(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/customers/${data.customerId}/drivers`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateDriver(data: IDriver): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/${data.customerId}/drivers/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerDrivers(customerId): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/customers/${customerId}/drivers`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  changeIsActive(id: string, customerId: string): Promise<any> {
    return this.http
      .patch(`${environment.api_base_url}/customers/${customerId}/drivers/${id}/status`, this.helperService.encryptData({}), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomers(data): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers`, {
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
      activeStatus: data.activeStatus,
      tags: data.tags || [],
      lastInvoiceDate: data.lastInvoiceDate || [],
      lastInvoiceStart: data.lastInvoiceStart,
      lastInvoiceEnd: data.lastInvoiceEnd,
      serviceType: data.serviceType,
      isFleetCustomer: data.isFleetCustomer,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomersList(data, body): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers/list`, {
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      // search: data.search,
      activeStatus: data.activeStatus,
      tags: data.tags || [],
      lastInvoiceDate: data.lastInvoiceDate || [],
      lastInvoiceStart: data.lastInvoiceStart,
      lastInvoiceEnd: data.lastInvoiceEnd,
      invoicePeriod: data.invoicePeriod,
      serviceType: data.serviceType,
      isFleetCustomer: data.isFleetCustomer,
    });
    return this.http.put(url, this.helperService.encryptData(body), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  // Get templates call
  createMarketingTemplates(data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingTemplate`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateMarketingTemplates(templateId, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/marketingTemplate/${templateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarrantyByItemSpec(data: any) {
    return this.http
      .post(`${environment.api_base_url}/warranties/getByItemSpec`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendPreviewSms(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingTemplate/sendPreviewSms`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendPreviewEmail(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingTemplate/sendPreviewEmail`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  scheduleFollowUp(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/scheduleFollowUps`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteScheduleFollowUp(followUpId: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/scheduleFollowUps/${followUpId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateFollowUpSchedule(followUpId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/scheduleFollowUps/scheduled/followUp/${followUpId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // Get templates call
  getMarketingTemplates(query: any, fetchStats: boolean = false): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingTemplate`, {
      ...query,
      fetchStats,
      sortField: query.sortField,
      sortOrder: query.sortOrder,
      limit: query.limit,
      skip: query.offset,
      searchQuery: query.searchQuery,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  // Get templates call
  getSingleMarketingTemplates(templateId): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/marketingTemplate/${templateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // Get campaign call
  getMarketingCampaign(data): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingTemplate`, {
      sortField: data.sortField,
      sortOrder: data.sortOrder,
      limit: data.limit,
      skip: data.offset,
      searchQuery: data.searchQuery,
      marketingCampaignId: data.marketingCampaignId,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createMarketingCampaign(data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingCampaign`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateMarketingCampaign(marketingCampaignId, data, updateExistingFollowup = false): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/marketingCampaign/${marketingCampaignId}?updateExistingFollowup=${updateExistingFollowup}`, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  launchMarketingCampaign(marketingCampaignId): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingCampaign/launch/${marketingCampaignId}`, this.helperService.encryptData(null), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateFollowUpCampaignStatus(campaignId: string, data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingCampaign/launch/followUp/${campaignId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  scheduleMarketingCampaign(marketingCampaignId, data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingCampaign/schedule/${marketingCampaignId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEndDateMarketingCampaign(marketingCampaignId, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/marketingCampaign/endDate/${marketingCampaignId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  cancelMarketingCampaign(marketingCampaignId): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/marketingCampaign/cancel/${marketingCampaignId}`, this.helperService.encryptData({}), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSingleMarketingCampaign(marketingCampaignId): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/marketingCampaign/${marketingCampaignId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getScheduledFollowUp(customerId: string, data: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/scheduleFollowUps/scheduled/followUp/${customerId}`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getScheduledFollowUpCount(followUpCampaignId: string, query: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/scheduleFollowUps/scheduled/count/${followUpCampaignId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getFollowUps(customerId: string, orderId: string = '', vehicleId: string = '', data: any = {}): Promise<any> {
    let url = `${environment.api_base_url}/marketingCampaign/followUp/campaigns/${customerId}`;

    if (orderId) {
      url += `/${orderId}`;
    }

    url = this.helperService.constructQueryParams(url, { ...data, vehicleId });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMileageVinInformation(orderId: string) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/vin/mileage/update/${orderId}`, {});
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateMileageVinInformation(orderId: string, data: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/vin/mileage/update/${orderId}`, {});
    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerVehicleFollowUps(customerId: string, vehicleId: string = '', { fetchCustomer = true, fetchVehicle = true }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/followUp/customer/campaigns`, {
      customerId,
      vehicleId,
      fetchVehicle,
      fetchCustomer,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingGroupContactStats(marketingCampaignId: string, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingGroup/contactStats/${marketingCampaignId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  searchServicesWithCount(search: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimate/service/used/count?search=${search}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMarketingCampaignList(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingCampaignMetrics(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/metrics`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getScheduledFollowupCampaignList(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/scheduleFollowUps`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getScheduledFollowupCampaignDetails(id): Promise<any> {
    const url = `${environment.api_base_url}/scheduleFollowUps/${id}`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateScheduledFollowup(id, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/scheduleFollowUps/${id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getFollowUpsForCampaign(query: any, campaignId: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/scheduleFollowUps/scheduled/followUp/list/${campaignId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSourceCampaigns(query: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/scheduleFollowUps/scheduled/source/campaign`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingGroupCustomerList(marketingCampaignId, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingGroup/customers/${marketingCampaignId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingCampaignAppointmentStats(campaignId): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/appointments/stats/${campaignId}`, {});

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerStats(data): Promise<any> {
    const body = {
      customerList: data.customerList || [],
      invoiceDateRange: data.invoiceDateRange || [],
      rOcreatedDate: data.rOcreatedDate,
      // invoiceServiceDateRange: data.invoiceDateRange || [],
      language: data.language || [],
      preferredLocations: data.preferredLocations || [],
      multiRangeRODate: data.multiRangeRODate,
      multiRangeInvDate: data.multiRangeInvDate,
      multiInvoiceDateRange: data.multiInvoiceDateRange,
      multiRoDateRange: data.multiRoDateRange,
    };
    // if (data.hasService) {
    //   delete body.invoiceDateRange;
    // } else {
    //   delete body.invoiceServiceDateRange;
    // }

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers/customerStats`, {
      customerTags: data.customerTags,
      customerActiveStatus: data.customerActiveStatus,
      serviceAuthStatus: data.serviceAuthStatus,
      customerFleet: data.customerFleet,
      serviceCategories: data.serviceCategories,
      searchTerms: data.searchTerms,
    });
    return this.http.put(url, this.helperService.encryptData(body), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOrderServiceList(data, body): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/service/list`, {
      limit: data.limit,
      offset: data.offset,
      sortField: data.sortField,
      sortOrder: data.sortOrder,
      search: data.search,
      customerFleet: data.customerFleet,
      serviceAuthStatus: data.serviceAuthStatus,
      listType: data.listType,
      authorization: data.authorization,
      serviceCategories: data.serviceCategories,
      preferredLocations: data.preferredLocations,
      searchTerms: data.searchTerms,
    });
    return this.http.put(url, this.helperService.encryptData(body), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOrderByCustVehicle(customerId: string, vehicleId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/customer/vehicle/${customerId}/${vehicleId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehiclePreviousHistory(vehicleId): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/previous/byVehicle/${vehicleId}`, {});

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSearchVehiclePreviousHistory(vehicleId, searchQuery): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/previous/byVehicle/${vehicleId}`, searchQuery);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingCampaignOrderStats(campaignId): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/orders/stats/${campaignId}`, {});

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingCampaignAppointments(campaignId, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/appointments/${campaignId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingCampaignOrders(campaignId, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/orders/${campaignId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getVehicleTireSizes(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireLookup/tire/sizes`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getVehicleTireSizeCombinations(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireConnect/size/combination`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getBins(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invLocations/tireStorage/bin`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createBin(data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/invLocations/tireStorage/bin`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateBin(data: any): Promise<any> {
    const url = `${environment.api_base_url}/invLocations/tireStorage/bin`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getStocks(data: any): Promise<any> {
    const query: any = {
      limit: data.limit || '',
      skip: data.offset || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: encodeURIComponent(data.search || ''),
      activeStatus: data.activeStatus || '',
      supplier: encodeURIComponent(data.supplier || ''),
      // unlinkInventoryItemOnly: data.unlinkInventoryItemOnly || '',
      locations: data.locations || [],
    };

    if (data.restockLevel) {
      query.restockLevel = data.restockLevel;
    }

    if (data.poType) {
      query.poType = data.poType;
    }

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invStocks`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTransferOrders(data: any): Promise<any> {
    const query: any = {
      limit: data.limit || '',
      skip: data.offset || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: encodeURIComponent(data.search || ''),
      activeStatus: data.activeStatus || '',
      senderWarehouse: data.senderWarehouse,
      receiverWarehouse: data.receiverWarehouse,
      status: data.status,
    };

    if (data.poType) {
      query.poType = data.poType;
    }

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/transferOrder/list`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPOsByItems(data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/purchaseOrders/byItems`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  returnItems(data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/invStocks/return/items`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // statement/create/bulkStatements
  createBulkStatements(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/statement/create/bulkStatements`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerStatementList(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/statement/customer/getStatementList`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getStockDetails(stockId: string): Promise<any> {
    const url = `${environment.api_base_url}/invStocks/${stockId}`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }
  getStockHistory(stockId: string): Promise<any> {
    const url = `${environment.api_base_url}/invStocks/adjustmentHistory/${stockId}`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getReOrderTrendsList(recordType: string, type: string, itemId: string, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invStocks/salesData/${type}/${recordType}/${itemId}`, query);
    // const url = `${environment.api_base_url}/invStocks/salesData/part/reOrder/${itemId}`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getInvDetailsFormLineItem(itemId: string): Promise<any> {
    const url = `${environment.api_base_url}/items/invetory/details/lineItem/${itemId}`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateStock(stockId: string, data: any): Promise<any> {
    const url = `${environment.api_base_url}/invStocks/update/${stockId}`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPurchaseOrder(data: any): Promise<any> {
    const query = {
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
      activeStatus: data.activeStatus,
      supplier: data.supplier,
      status: data.poStatus,
      itemType: data.itemType,
      payableStatus: data.payableStatus,
      paymentStatus: data.paymentPayableStatus,
      bulkPaymentList: data.bulkPaymentList,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/purchaseOrders`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTireConnectOnlineOrder(data: any): Promise<any> {
    const url = `${environment.api_base_url}/partsOrdering/${data.id}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOnlineTireConnectOrder(data: any): Promise<any> {
    const query = {
      poGroupId: data.poGroupId || '',
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
      activeStatus: data.activeStatus,
      supplier: data.supplier,
      status: data.poStatus,
      payableStatus: data.payableStatus,
      paymentStatus: data.paymentPayableStatus,
      bulkPaymentList: data.bulkPaymentList,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partsOrdering/online`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOnlineOrder(data: any): Promise<any> {
    const query = {
      poGroupId: data.poGroupId || '',
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
      activeStatus: data.activeStatus,
      supplier: data.supplier,
      status: data.poStatus,
      payableStatus: data.payableStatus,
      paymentStatus: data.paymentPayableStatus,
      bulkPaymentList: data.bulkPaymentList,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/purchaseOrders/online`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateOnlineOrderTireConnectInvRef(data: UpdateOnlineOrderInvRefPayload): Promise<any> {
    const url = `${environment.api_base_url}/partsOrdering/inventory/link/item`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  removeOnlineOrderTireConnectInvRef(data: UpdateOnlineOrderInvRefPayload): Promise<any> {
    const url = `${environment.api_base_url}/partsOrdering/inventory/unlink/item`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateOnlineOrderInvRef(data: UpdateOnlineOrderInvRefPayload): Promise<any> {
    const url = `${environment.api_base_url}/purchaseOrders/link/inventory/item`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  removeOnlineOrderInvRef(data: UpdateOnlineOrderInvRefPayload): Promise<any> {
    const url = `${environment.api_base_url}/purchaseOrders/unlink/inventory/item`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSupplierPurchaseOrders(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/suppliers/pos/${data.supplier}`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }
  getPartReturnOrder(data: any): Promise<any> {
    const query = {
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
      activeStatus: data.activeStatus,
      supplier: data.supplier,
      status: data.returnPart,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/purchaseOrders`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAllCustomers(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/customers`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomer(
    id: string,
    data: {
      fetchTags?: boolean;
      fetchVehicles?: boolean;
      populatePricingMatrix?: boolean;
      populateContact?: boolean;
      populateTaxCodeOverRide?: boolean;
      selectedField?: string;
      mergeCustomer?: boolean;
    } = {},
  ): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers/${id}`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerChargeAccountDetails(
    id: string,
    data: {
      chargeAccountDetails?: boolean;
    } = {},
  ): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers/${id}`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getContactInfo(
    contactEntityId: string,
    populateEntity = false,
    params: { source?: string; notificationId?: string; contactEntityType?: string } = { source: '', notificationId: '', contactEntityType: 'customer' },
  ): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/contacts/${contactEntityId}`, {
      populateEntity,
      contactEntityType: params.contactEntityType,
      source: params.source,
      notificationId: params.notificationId,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateContacts(customerId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/contacts/${customerId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  // customers/updateCommunicationPreference/:customerId

  updateCommunicationPreference(customerId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/updateCommunicationPreference/${customerId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCustomerLocation(customerId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/preferred-location/update/${customerId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  resetCustomerSubscription(data: { customerId: string }): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/contacts/reset/subscription`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  changeUserPassword(userId: string, password: string, isOnboarding: boolean = false, geoInfo: any) {
    return this.http
      .post(
        `${environment.api_base_url}/changeUserpassword?isOnboarding=${isOnboarding}`,
        {
          userId,
          password,
          isOnboarding,
          geoInfo,
        },
        { headers: this.getHeaders() },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isSupportAdminAccount() {
    return this.http
      .get(`${environment.api_base_url}/isSupportAdminAccount`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerIsUnique(data: any, customerId: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/customers/unique/${customerId || ''}?${data}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isItemTitleUnique(data: { title: string; code?: string; type: string }, itemId: string): Promise<any> {
    const payload = {
      title: encodeURIComponent(data.title),
      code: encodeURIComponent(data.code),
      type: data.type,
    };

    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/items/unique/${itemId}`, payload), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  // isItemTitleUnique(data: any, itemId: string = '', type = '', code: string = ''): Promise<any> {
  //   return this.http
  //     .get(`${environment.api_base_url}/items/unique/${itemId || ''}?type=${type}&title=${data.replace(/&/g, '%26')}&code=${encodeURIComponent(code)}`, { headers: this.getHeaders() })
  //     .toPromise()
  //     .then(this.successHandler.bind(this))
  //     .catch(this.errorHandler.bind(this));
  // }

  isCategoryUnique(data: { name: string; type: string }, itemId: string): Promise<any> {
    const payload = {
      name: encodeURIComponent(data.name),
      type: encodeURIComponent(data.type),
    };
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/tags/unique/${itemId}`, payload), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCustomerFleetSettings(customerId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/custom-fleet-settings/${customerId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCustomer(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCustomerArLimit(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/updateChargeAccountLimit/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  customerSearch(query): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/customers?search=${query}&activeStatus=all`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  estimateSearch(query): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates?${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireStorageLocation(search: string = '', concatBins?: boolean) {
    let url = `${environment.api_base_url}/invLocations/tireStorage/location?search=${search}`;
    if (concatBins) {
      url = `${environment.api_base_url}/invLocations/tireStorage/location?search=${search}&getBins=${concatBins}`;
    }
    return this.http
      .get(url, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTireStorageLocation(data: any) {
    return this.http
      .post(`${environment.api_base_url}/invLocations/tireStorage/location`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireStorageLocation(data: any) {
    return this.http
      .put(`${environment.api_base_url}/invLocations/tireStorage/location`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTag(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tags`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTag(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/tags/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkOrderPartsTechStatus(orderId: string) {
    return this.http
      .get(`${environment.api_base_url}/partstech/status/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAdHocPartById(partId: string, query: any = {}) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/adHocItems/${partId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteAdHoc(query: any = {}) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/adHocItems`, query);
    return this.http.delete(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  /** fetch tags from the db  */
  getTags(type: string = '', searchTerm: string = ''): Promise<Object | any[]> {
    return this.http
      .get(`${environment.api_base_url}/tags?type=${type}&searchTerm=${encodeURIComponent(searchTerm)}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSearchTags(type: string = '', queryData): Promise<Object | any[]> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tags`, {
      ...queryData,  // pagination , searchTerm and skip
      type
    });
  
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getGroupedSalesCode(queryParams: any = {}) {
    if (queryParams?.searchTerm) {
      queryParams.searchTerm = encodeURIComponent(queryParams.searchTerm);
    }
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/categories/salesCode/grouped`, queryParams);
    return this.http
      .get(`${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getUnassignedSalesCodeItems(orderId: string) {
    return this.http
      .get(`${environment.api_base_url}/estimate/items/unassigned/salesCode/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /** fetch tags from the db  */
  getMultipleTags(types?: string[]) {
    return this.http
      .put(`${environment.api_base_url}/multiple/tags`, this.helperService.encryptData(types), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /** Order Details */
  updateOrderDetails(estimateId: string, data: IUpdateOrderDetailsData) {
    return this.http
      .put(`${environment.api_base_url}/estimates/details/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderDates(estimateId: string, data) {
    return this.http
      .put(`${environment.api_base_url}/estimates/UpdateOrderDates/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /** Order Services */
  createOrderService(estimateId: string, data) {
    const enterprise = this.dataService.getEnterprise();
    return this.http
      .put(`${environment.api_base_url}/estimates/service/${estimateId}`, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
        params: {
          enterprise,
        },
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderServiceTitleNotes(serviceId: string, estimateId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/title/update/${serviceId}/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateLineServiceNotes(serviceId: string, estimateId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/update/${serviceId}/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderServices(estimateId: string, data: any, checkPartsTechStatus: boolean = false) {
    return this.http
      .put(`${environment.api_base_url}/estimates/services/update/${estimateId}?checkPartsTechStatus=${checkPartsTechStatus}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireDotNumber(data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimate/update/tire/dot`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateServiceItemClassification(data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimate/update/item/classification`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  assignSalesCodeToServiceItems(data: any, orderId: string) {
    return this.http
      .put(`${environment.api_base_url}/estimate/update/item/classification/bulk/${orderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  assignTechToService(serviceId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/assignTech/${serviceId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  bulkAssignTechToService(estimateId: string, data: any, param = { source: '', notificationId: '' }) {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/bulkAssignTech/${estimateId}?source=${param.source}&notificationId=${param.notificationId}`, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  bulkAssignTechnician(estimateId: string, data: any, param = { source: '', notificationId: '' }) {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/assign/multiTechs/${estimateId}?source=${param.source}&notificationId=${param.notificationId}`, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteOrderService(serviceId: string, estimateId: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/estimates/service/remove/${serviceId}/${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  permanentlyDeleteTrashOrders(): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/estimate/permanent/delete/all`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  exportAccountExport(data: any): Promise<any> {
    const url = `${environment.api_base_url}/reports/exportAccountingReport`;
    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteSelectedOrders(orderIds: string[]): Promise<any> {
    const url = `${environment.api_base_url}/estimate/bulk/delete`;
    return this.http.put(url, { orderIds }, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteSelectOrderPermanently(orderIds: string[]): Promise<any> {
    const url = `${environment.api_base_url}/estimate/permanent/delete/selected`;
    return this.http.put(url, { orderIds }, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  restoreSelectOrder(orderIds: string[]): Promise<any> {
    const url = `${environment.api_base_url}/estimate/deleted/restore`;
    return this.http.put(url, { orderIds }, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createOrderConcern(data: any): Promise<any> {
    const url = `${environment.api_base_url}/concern`;
    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateOrderConcern(id: string, data): Promise<any> {
    const url = `${environment.api_base_url}/concern/${id}`;
    return this.http.put(url, this.helperService.encryptData(data), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateOrderConcernList(data): Promise<any> {
    const url = `${environment.api_base_url}/concern`;
    return this.http.put(url, this.helperService.encryptData(data), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOrderConcerns(query: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/concern`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteOrderConcern(id: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/concern/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /** Order status */
  updateOrderStatus(estimateId: string, data: IUpdateOrderStatusData) {
    return this.http
      .put(`${environment.api_base_url}/estimates/status/orderStatus/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderWorkflow(estimateId: string, data: IUpdateOrderWorkflowData) {
    return this.http
      .put(`${environment.api_base_url}/estimates/status/workflowStatus/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderAuthorization(estimateId: string, data: IUpdateOrderAuthorizationData) {
    return this.http
      .put(`${environment.api_base_url}/estimates/status/authorization/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderRestriction(estimateId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/restriction/status/update/${estimateId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInvoiceOrderRestriction(estimateId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/invoice/request/access/${estimateId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteTag(tagId: any): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/tags/${tagId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteSalesGroup(salesGroupId: any): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/tags/salesGroup/${salesGroupId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  citySearch(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/cities`, data);

    return this.http.get(url).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  stateSearch(query: any = {}): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/states`, query), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  stateFilterByCity(query: any = {}): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/cities/filter`, query), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isSwipeRequestPending(estimateId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/isSwipeRequestPending/${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQboTaxExemptReasons(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/qboTaxExemptReasons`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getZip(code: any): Promise<any> {
    return this.http.get(`${environment.api_base_url}/zipcode/${code}`, {}).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getApiSettings(type: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/apiSettings/type/${type}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchMotorVehicleEndCustomerRequest(token: string, search: string, stateCode: string = '', searchInCarfax: boolean = true): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/vehicles/motor/searchPreview`, {
      search,
      stateCode,
      searchInCarfax,
    });
    return this.http
      .get(url, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchMotorVehicle(search: string, stateCode: string = '', searchInCarfax: boolean = true): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/vehicles/motor/search`, {
      search,
      stateCode,
      searchInCarfax,
    });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  searchInAllMotorGens(search: string, stateCode: string = '', searchInCarfax: boolean = true): Promise<any> {
    const isMotorGen2Enabled = this.dataService.isMotorGen2Enabled;
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/vehicles/motor/search`, {
      search,
      stateCode,
      searchInCarfax,
      isMotorGen2Enabled,
    });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCompanyTaxesList(): Promise<any> {
    const url = `${environment.api_base_url}/taxCodes/taxes/data`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getQtqAdonServices(): Promise<any> {
    const url = `${environment.api_base_url}/services/qtqServices`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getUnSyncResults(vehicleId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/vehicles/unsync/motor-carfax/${vehicleId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  unSyncVehicleUpdate(vehicleId: string, data): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/vehicles/unsync/update/${vehicleId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotor(url: any) {
    return this.http.get(url, { headers: this.headers }).toPromise();
  }

  getTireConnectTiresForStockReplenishment(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireconnect/inventory/byPartNumbers`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getFETFromTireConnect(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireconnect/inventory/byPartNumbers`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createVehicles(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/vehicles`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehicleById(vehicleId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/vehicles/${vehicleId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isVehicleVinExists(vehicleId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/vehicles/vin/exists/${vehicleId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isVehicleNameUnique(data: { vehicleName: string; vehicleId?: string; customerId: string }): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/vehicles/fleet/unique`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isDuplicateVehicle(search: string, vin: boolean = false, licensePlate: boolean = false): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/vehicles/added/search`, {
      search,
      vin,
      licensePlate,
    });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getActiveService(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/order/timesheet/getLastestService`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehicleCarfaxHistory(vehicleId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/vehicles/carfax/history/${vehicleId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehiclesByCustomer(customerId: string, fetchTags: boolean = false, fetchCustomer: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/vehicles/customer/${customerId}?fetchTags=${fetchTags}&fetchCustomer=${fetchCustomer}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerVehicle(customerId: string, query: any = {}): Promise<any> {
    if(customerId) {
      const url = this.helperService.constructQueryParams(`${environment.api_base_url}/vehicles/customer/pagination/${customerId}`, query);

      return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
    }
    
  }

  updateVehicle(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/vehicles/${data._id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateBulkVehicle(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/bulk/vehicles`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateBulkDrivers(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/customers/${data.customerId}/drivers/bulkUpdates`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateMotorConnection(vehicleId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/vehicle/update/motor/${vehicleId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllEmployees(data?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/employees`, data);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getKanbanFilterData(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/kanbans/filter/structure`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTemplatesByType(type: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/templates/type/${type}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  getTemplatesByTypeLang(type: any, lang: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/templates/type/${type}?language=${lang.toUpperCase()}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLatestCustomerOrder(customerId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/latest/${customerId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMultipleTemplates(data: any, isNeedCompany: boolean = false, query: any = {}): Promise<any> {
    const queryParams = query || {};

    if (isNeedCompany) {
      queryParams.isNeedCompany = isNeedCompany;
    }

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/templates/type`, queryParams);

    return this.http
      .put(url, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSendEstimateData(data: { customerId: string; orderId: string; sendType: string; populateOrder?: boolean }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/send/data/${data.orderId}`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getEmailTemplatesByType(type: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/emails/type/${type}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateAppointment(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/appointments/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateApptVehicle(appointmentId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/appointments/changevehicle/${appointmentId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateAppSettings(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/company/app/settings`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateShopAppSettings(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/shop/settings`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateMitchell1AppSettings(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/mitchell/update/settings`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateFeatureFlag(data: any, companyId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/company/app/featureFlag/${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateGoogleReviewLink(link: any, companyId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/admin/googleReviewLink/${companyId}`, this.helperService.encryptData({ link: link }), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateAppointmentColorLabel(data: any, oldColor?): Promise<any> {
    let url = ``;
    if (oldColor && data._id) {
      url = `/?colorId=${data._id}&oldColor=${encodeURIComponent(oldColor)}`;
    } else {
      url = ``;
    }
    return this.http
      .put(`${environment.api_base_url}/appointments/color/update${url}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTemplate(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/templates/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEmailTemplate(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/emails/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteAppointment(data: any): Promise<any> {
    return this.http
      .request('delete', `${environment.api_base_url}/appointments/${data._id}`, {
        headers: this.getHeaders(),
        body: data,
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTemplate(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/templates`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTaxRate(data: any, companyId: string = ''): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/taxCode/taxRate?companyId=${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTaxRate(data: any, companyId: string = ''): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/tax/rate?companyId=${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTaxCode(data: any, companyId: string = ''): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/taxCode/code?companyId=${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  handleAssociateTax(data: any, taxCodeId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/tax/code/${taxCodeId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createEmailTemplate(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/emails`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createAppointment(data: any, param = { source: '', notificationId: '' }): Promise<any> {
    const { userName } = this.helperService.getCompany();
    if (data.estimate && data.estimate.estimateId) {
      data.estimate.estimateBy = userName;
      data.estimate.createdAt = new Date();
    }
    return this.http
      .post(`${environment.api_base_url}/appointments?source=${param.source}&notificationId=${param.notificationId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTires(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/items/create/tire`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTireStorage(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/items/tire/storage`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireStorageList(data: any): Promise<any> {
    const query = {
      limit: data.limit,
      customerId: data.customerId,
      page: data.offset,
      vehicleId: data.vehicleId,
      sortOrder: data.sortOrder,
      sosortField: data.sortField,
      offset: data.offset,
      storageDate: data.storageDate,
      removalDate: data.removalDate,
      location: data.supplier,
      status: data.returnPart,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/tireStorage/list`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAppointments(data: any, apptReq): Promise<any> {
    const urls = this.helperService.constructQueryParams(`appointments/date/${data.startDate}/${data.endDate}`, apptReq.apptReq ? apptReq : {});
    return this.http
      .put(
        `${environment.api_base_url}/${urls}`,
        { techs: data.techs },
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAppointmentById(appointmentId: string, vehicleId?: string): Promise<any> {
    let url;
    vehicleId ? (url = `${appointmentId}/${vehicleId}`) : (url = appointmentId);
    return this.http
      .get(`${environment.api_base_url}/appointments/${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getApptLogs(appointmentId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appointments/logs/${appointmentId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAppointmentInitData(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appointments/init`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderInitData(orderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimate/init/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCompanyDetails(companyId: any, query: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/companies/${companyId}?${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  gettingOrderFeesData(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/company/app/order/fees`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  gettingSupplierAccPayableTerms(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/company/accPayableTerms`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  gettingPaymentAccPayableTerms(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/company/accPayableTerms/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAppointmentRequestSetting(companyId: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/companies/appointmentSettings`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createAppointmentRequestFromApp(token, body): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/app/appointmentRequests`, this.helperService.encryptData(body), { headers: !token ? this.getHeaders() : this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createAppointmentRequest(token, body): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/appointmentRequests`, this.helperService.encryptData(body), { headers: !token ? this.getHeaders() : this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateAppointmentRequest(body, decline?): Promise<any> {
    let url = '';
    if (decline) {
      url = this.helperService.constructQueryParams(`${environment.api_base_url}/appointmentRequests/${body._id}`, decline);
    } else {
      url = `${environment.api_base_url}/appointmentRequests/${body._id}`;
    }
    return this.http.put(`${url}`, this.helperService.encryptData(body), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAppointmentRequest(data?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appointmentRequests`, data);
    return this.http
      .get(`${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /*
  GET: Appointment Trial Status Response
  */
  getAppointmentRequestTrial(): Promise<any> {
    const url = `${environment.api_base_url}/appointmentRequests/getApptReqTrialStatus`;
    return this.http
      .get(`${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAppointmentRequestById(id: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appointmentRequests/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEndCustomerRequest(token: string, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appointmentRequests/init/data`, query);
    return this.http
      .get(url, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  subscriptionRequest(companyId: string, preferredContact): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/companies/subscriptionRequest/${companyId}`, this.helperService.encryptData(preferredContact), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  trialExtensionRequest(companyId: string, preferredContact): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/companies/trialExtensionRequest/${companyId}`, this.helperService.encryptData(preferredContact), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCompaniesByAdmin(companyId = '', query?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/admin/companies/${companyId}`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCompaniesPaginationByAdmin(companyId = '', query?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/admin/pagination/companies/${companyId}`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // For company
  getShopListsForCompany(query = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/list/company`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getShopDetailForCompany(companyId: string, locationId: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/detail/company`, { companyId, locationId });
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // For shops
  getShopLists(query = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/list`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getShopDetail(companyId: string, locationId: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/detail`, { companyId, locationId });
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  switchLocation(data: { currentShopId: string }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/switch`, data);
    return this.http
      .put(url, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCompaniesForFeatureList(companyId = '', query?): Promise<any> {
    // if tenantBetaFlag its add in featureList
    if (query && query.tenantBetaFeatureSelected && query.tenantBetaFeatureSelected.length) {
      query.featureList = query.featureList.concat(query.tenantBetaFeatureSelected);
    }
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/admin/feature/companies${companyId ? `/${companyId}` : ''}`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getShop(): Promise<any> {
    const url = `${environment.api_base_url}/shop`;
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getShopForCustomer(data: string): Promise<any> {
    const url = `${environment.api_base_url}/shop/customer`;
    return this.http
      .get(url, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getShopsFeatureList(companyId: string, query: any = {}): Promise<any> {
    // if tenantBetaFlag its add in featureList
    // if (query && query.tenantBetaFeatureSelected && query.tenantBetaFeatureSelected.length) {
    //   query.featureList = query.featureList.concat(query.tenantBetaFeatureSelected);
    // }
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/list/company/feature/${companyId}`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Get companies stats
   */
  getCompaniesStats(query?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/admin/stats/companies`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorCategories(vehicleBaseId: string): Promise<any> {
    const url = `${environment.api_base_url}/motor/services/default?vehicleBaseId=${vehicleBaseId}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorCategoriesNewGen(vehicleId: string, categories = 'all', search = ''): Promise<any> {
    const url = `${environment.api_base_url}/motors/getCategories/${vehicleId}?selectedCategory=${categories}&search=${search}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorSubCategoriesNewGen(vehicleId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/getCategories/${vehicleId}`;
    url += `?${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorCategoriesService(vehicleBaseId: string, systemID: string, groupID: string, query: string, callTimestamp: number = 0): Promise<any> {
    let url = `${environment.api_base_url}/motor/services/worktime?`;
    url += `vehicleBaseId=${vehicleBaseId}&`;
    url += `systemID=${systemID}&`;
    url += `groupID=${groupID}&`;
    url += `callTimestamp=${callTimestamp}&`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // /motor/getServices
  getMotorCategoriesServiceNewGen(vehicleId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/getServices/${vehicleId}?`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorRepairGuideCounts(vehicleBaseId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/guides/count?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorProcedures(vehicleBaseId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/procedures?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorProceduresDetails(vehicleBaseId: string, query: string, data: any): Promise<any> {
    let url = `${environment.api_base_url}/motors/procedures/details?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http.put(url, this.helperService.encryptData(data), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMotorSpecsDetails(vehicleBaseId: string, query: string, data: any): Promise<any> {
    let url = `${environment.api_base_url}/motors/specifications/details?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http.put(url, this.helperService.encryptData(data), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMotorWiringDetails(vehicleBaseId: string, query: string, data: any): Promise<any> {
    let url = `${environment.api_base_url}/motors/wirings/details?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http.put(url, this.helperService.encryptData(data), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMotorBulletinDetails(vehicleBaseId: string, query: string, data: any): Promise<any> {
    let url = `${environment.api_base_url}/motors/bulletins/details?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http.put(url, this.helperService.encryptData(data), { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMotorSpecifcations(vehicleBaseId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/specifications?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorWiring(vehicleBaseId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/wirings?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorBulletins(vehicleBaseId: string, query: string): Promise<any> {
    let url = `${environment.api_base_url}/motors/bulletins?`;
    url += `motorVehicleId=${vehicleBaseId}&`;
    url += `${query}`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAdditionalMotorService(query: any, callTimestamp?: number): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/motor/services/additional/worktime`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMotorIncludedOperations(query: any, callTimestamp?: number): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/motor/services/included/operations`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getComponentDiagrams(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/diagram/component`;

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getComponentLocationDetails(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/component/location/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getComponentSpecsDetail(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/component/specs/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getProceduresDetail(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/component/procedures/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getBulletinDetail(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/component/bulletin/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWiringDiagramDetails(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/component/wiring/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getIllustrationDetails(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/component/illustration/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTroubleshootCodeBySearch(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/troubleshoot/code`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTroubleshootDetails(data: any): Promise<any> {
    const url = `${environment.api_base_url}/motor/troubleshoot/code/details`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMotorMaintainanceByInterval(query: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/motor/services/maintainance/interval`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMaintenanceForInterval(query: any, data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/motor/services/maintainanceforInterval`, query);

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMotorMaintainanceByIndicator(query: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/motor/services/maintainance/indicator`, query);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchMaintenance(query: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/motor/services/search/maintainance`, query);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDefaultMotorPricingsAndTags(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/motor/default/pricing/tag`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllEstimates(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates?limit=${data.limit}&skip=${data.offset}&sort=${data.sortField}&order=${data.sortOrder}&search=${data.search}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllOrderList(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/orderList`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getStatementInvoicesList(query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/statement/invoices`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCompanyTax(companyId: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/companies/tax/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isTwilioAccountValid(accountSID: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/companies/isTwilioAccountValid`, { accountSID }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  filterEestimates(filterQuery): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates?search=&status=${filterQuery}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehicleRecomendedParts(vehicle: any, options: { fetchSizes: boolean }): Promise<any> {
    const query = { ...vehicle, ...options };

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireLookup/tire/sizes`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireHubInventory(options: { tireSize?: string; brand?: string; season?: string; loadIndex?: string; speedRating?: string; fetchQTQData?: boolean }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/inventory`, options);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  getTireHubInventoryItem(options: { orderItemId?: string; warehouseId?: string; quantity?: string }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/inventory/item/details`, options);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireConnectInventory(options: {
    tireSize?: string;
    brand?: string;
    season?: string;
    loadIndex?: string;
    speedRating?: string;
    tireWidth: string;
    tireProfile: string;
    fetchQTQData?: boolean;
  }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireconnect/inventory`, options);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireConnectCartDetails(tireId: string, data = {}): Promise<any> {
    const url = `${environment.api_base_url}/tireconnect/inventory/tire/${tireId}`;

    return this.http
      .get(this.helperService.constructQueryParams(url, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPartsOrderingQoute(orderId: string, data: { source: string; sessionId: string; fetchUnOrdered?: boolean }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/qoute/${orderId}`, data);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTCPartsOrderingQoute(orderId: string, data: { source: string; sessionId: string; fetchUnOrdered?: boolean }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireConnect/qoute/${orderId}`, data);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTireConnectInventoryPO(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partsOrdering/create`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createRepairOrder(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/estimates/createRepairOrder`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createRepairOrderGlobalCart(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/estimates/createRepairOrderQTQGlobal`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addGlobalCartToEstimate(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/estimates/createRepairOrderQTQGlobal`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  modifyGlobalCartOnEstimate(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/estimates/modifyCartQTQGlobal`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveTireHubCart(orderId: string, cart: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tirehub/cart/${orderId}`, { cart }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveTireConnectCart(orderId: string, cart: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireConnect/cart/${orderId}`, cart, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireConnectSupplierField(data: { supplierName: string; tireIds?: any; quantities?: any }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireConnect/supplier/field/${data.supplierName}`, {
      tireIds: data.tireIds,
      quantities: data.quantities,
    });

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireAttr(data: { tireWidth?: string; tireProfile?: string } = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireLookup/tire/attr`, data);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireHubTireAttr(data: { tireWidth?: string; tireProfile?: string } = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/combination`, data);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireConnectTireAttr(data: { tireWidth?: string; tireProfile?: string } = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireconnect/size/combination`, data);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireConnectConnectedSuppliers(): Promise<any> {
    const url = `${environment.api_base_url}/tireconnect/supplier/connected`;

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimates(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerPreferredLanguage(customerId): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/localization/getCustomerPreferredLanguage/${customerId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateById(
    estimateId: string,
    isTaxCodeNeeded: boolean = false,
    isServiceTimesheetsNeeded: boolean = false,
    data: {
      isMakeSessionEditable: boolean;
      browserSessionId?: number;
      isStockedItemDetailsNeeded?: boolean;
      vehicleLimit?: number;
      vehicleSkip?: number;
    } = { isMakeSessionEditable: false, isStockedItemDetailsNeeded: false },
  ): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/${estimateId}`, {
      isTaxCodeNeeded: isTaxCodeNeeded,
      isServiceTimesheetsNeeded: isServiceTimesheetsNeeded,
      ...data,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getImportedRO(estimateId: string, populateEntities: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/importOrders/${estimateId}?populateEntities=${populateEntities}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAdjustmentOrderItem(orderId: string, adjustmentOrderId: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/items/adjustments`, {
      orderId,
      adjustmentOrderId,
    });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getImportedROForCustomer(estimateId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/importOrders/customerEstimate/${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehicleOrderHistory(vehicleId: string, query: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/vehicle/order/history/${vehicleId}?query=${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createStatement(data: any = {}): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/statement`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDocumentById(documentId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/documents/signedUrl/${documentId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDocumentByKey(key: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/documents/byKey/signedUrl?key=${key}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getStatementById(statementId: string, populateCustomer: boolean = false, populateInvoice: boolean = false, fetchLogs: boolean = false, populateVehicle: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/statement/${statementId}?populateCustomer=${populateCustomer}&populateInvoice=${populateInvoice}&fetchLogs=${fetchLogs}&populateVehicle=${populateVehicle}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getBulkPaymentById(bulkPaymentId: string, populateInvoices: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/bulkPayment/${bulkPaymentId}?populateInvoices=${populateInvoices}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadBulkPaymentPdf(bulkPaymentId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/bulkPayment/download/pdf/${bulkPaymentId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getBulkPaymentByToken(data: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/bulkPayment/print/preview`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getStatementByToken(data: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/statement/print/preview`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadBulkPaymentPdfDocument(data: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/bulkPayment/print/download`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadStatementPdfDocument(data: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/statement/print/download`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  generateStatementPdfLink(companyId: string, statementId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/statement/print/pdf/${companyId}/${statementId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPrintReportLogs(type, data?) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs/getByType/${type}`, {
      filterBy: data?.filterBy || [],
      search: data?.search || '',
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  downloadStatementZip(statementId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/statement/zip/url/${statementId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadStatementPdf(companyId: string, statementId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/statement/download/pdf/${companyId}/${statementId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  postMultipleInvoiceToQbo(companyId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/invoice/multi/qbo/sync`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getStatementList(query: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/statement/customer/getStatementList`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateStatementById(statementId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/statement/${statementId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPurchaseOrderTireConnectById(purchaseOrderId: string, isTaxCodeNeeded: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/partsOrdering/po/${purchaseOrderId}?isTaxCodeNeeded=${isTaxCodeNeeded}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPurchaseOrderById(purchaseOrderId: string, isTaxCodeNeeded: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/purchaseOrders/${purchaseOrderId}?isTaxCodeNeeded=${isTaxCodeNeeded}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTransferStockOrderById(transferOrderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/transferOrder/${transferOrderId}?isTaxCodeNeeded=true`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPurchaseOrder(data: any = {}): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/purchaseOrders?itemId=${data.itemId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTransferStockOrder(data: any = {}): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/transferOrder`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTransferStockOrder(transferOrderId: string, data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/transferOrder/${transferOrderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPurchaseOrderWithItemList(data: any = {}): Promise<any> {
    const list: any = {};
    list.items = data;
    return this.http
      .post(`${environment.api_base_url}/purchaseOrders`, this.helperService.encryptData(list), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatepurchaseOrder(po: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/purchaseOrders/${po}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateByIdCustomer(data, isShowPaymentDetails = false): Promise<any> {
    let url = `${environment.api_base_url}/estimates/customerEstimatePreview?`;
    url += `isShowPaymentDetails=${isShowPaymentDetails}`;
    return this.http
      .get(url, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getInspectionByIdCustomer(data, isShowPaymentDetails = false): Promise<any> {
    let url = `${environment.api_base_url}/estimates/downloadPdf?`;
    url += `isShowPaymentDetails=${isShowPaymentDetails}`;
    return this.http
      .get(url, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateByIdCustomerTokenPreview(data, isShowPaymentDetails = false, lang?): Promise<any> {
    let url = `${environment.api_base_url}/estimates/customerEstimate?`;
    url += `isShowPaymentDetails=${isShowPaymentDetails}`;
    if (lang) {
      url += `&lang=${lang}`;
    }
    return this.http
      .get(url, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarrantyPolicyTermsAndConditions(data: any, type: string): Promise<any> {
    let url = `${environment.api_base_url}/templates/type/${type}`;
    return this.http
      .get(url, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarrantyPolicyDisclaimer(itemId: string) {
    return this.http
      .get(`${environment.api_base_url}/warranties/getOrdersByItemId/${itemId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarrantiesOrdersByItemId(data: any) {
    return this.http
      .post(`${environment.api_base_url}/warranties/getOrdersByItemId`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireStoragePrintData(token: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/pdf/tireStorage/data`, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerDeferredService(customerId: string, vehicleId: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/services/customer/deferredService/${customerId}/${vehicleId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteCustomerDeferredService(serviceId: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/services/deferredService/${serviceId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderDeferredService(vehicleId: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/services/order/deferredService/${vehicleId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReviewByIdCustomer(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/reviewPreview`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReviewByIdCustomerPreview(estimateId, paymentId): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/review?estimateId=${estimateId}&paymentId=${paymentId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateByToken(token: string, id: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/customerEstimate/${id}`, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateByIdForPreview(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/customerEstimate/${data}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateByIdForTokenPreview(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/customerEstimatePreview/${data}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchOrderActivities(orderId: string, { search = '', iShowAllActivities = '' }: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs/orderactivity/search/${orderId}`, {
      search,
      iShowAllActivities,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createEstimates(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/estimates`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTireStorageEstimate({ customerId, vehicleId, tireStorageIds, createType, tireSwape = false, browserSessionId = Date.now(), isTireScheduleForReturn = false, estimateId = null, createFromTireStorageInventory = false }): Promise<any> {
    const url = `${environment.api_base_url}/estimates/tireStorage/create`;

    return this.http
      .post(url, this.helperService.encryptData({ customerId, vehicleId, tireStorageIds, createType, tireSwape, browserSessionId, isTireScheduleForReturn, estimateId, createFromTireStorageInventory }), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToPartsTech(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partstech/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToMitchell1(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/mitchell/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectPartstech(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/partstech/disconnect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectMitchell1(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/mitchell/disconnect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToNexpart(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/nexpart/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectNexpart(data: { sellerId?: string }): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/nexpart/disconnect`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectMFCVehicles(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/mfc/${data._id}/vehicles/disconnect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToMyFleetCenter(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/mfc/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectToMyFleetCenter(data = {}): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/mfc/disconnect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMyFleetCenterIntegrationStatus(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/mfc/status`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getFleetCatalogCode(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/mfc/catalogCode`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateFleetCatalogCode(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/mfc/catalogCode/update`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchMyFleetCenter(searchString = '', type = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/mfc/search?search=${searchString}&includeVehiclesInResults=${type}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  postToMyFleetCenter(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/mfc/${data.orderId}/invoice/upload`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMitchell1GuideLink(data: { vehicleId?: string; searchTerm: string }): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/mitchell/mitchell1/interface`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMitchell1EstimatorToken(data: { vehicleId?: string; searchTerm?: string } = {}): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/mitchell/mitchell1/estimator`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToTireHub(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tirehub/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToTireConnect(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireconnect/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectTireHub(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/tirehub/disconnect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectTireConnect(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/tireconnect/disconnect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToRepairLink(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/repairlink/connect`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectRepairLink(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/repairlink/disconnect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectSpeedDial(sellerId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/speeddial/disconnect?sellerId=${sellerId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createSpeedDialOrder(data): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/speeddial/init/order`, { ...data });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  submitSpeedDialOrder(data, orderId, isManualMarkOrdered = false): Promise<any> {
    let orderderMethod = '';
    if (isManualMarkOrdered) {
      orderderMethod = 'orderMethod=manual';
    }
    return this.http
      .post(`${environment.api_base_url}/speeddial/submit/order?orderId=${orderId}&${orderderMethod}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  markOrderedManualPlacement(orderId, internalRefrenceId): Promise<any> {
    const url = `${environment.api_base_url}/speeddial/markForManualPlacement/${orderId}?speedDialReferenceId=${internalRefrenceId}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createManualOrder(payload: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/manualOrder/create`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  openPartsTechStockCart(data: { poId?: string; sessionId?: string; items?: [{ partNumber?: string }] }): Promise<any> {
    const url = `${environment.api_base_url}/partstech/open/stock/qoute${data.poId ? `/${data.poId}` : ''}`;
    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  openNexPartStockCartDeferred(data): Promise<any> {
    const url = `${environment.api_base_url}/nexpart/create/qouteWithParts`;
    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  openPartsTechStockCartDeferred(data): Promise<any> {
    const url = `${environment.api_base_url}/partstech/create/qouteWithParts`;
    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncPartsTechInventoryOrder(poGroupId: string): Promise<any> {
    const url = `${environment.api_base_url}/partstech/inventory/sync/${poGroupId}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateManualOrder(payload: any, qouteId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/manualOrder/update/${qouteId}`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateNextandPartsTechPayment(payload: any, estimateId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimate/orderLevel/update/${estimateId}`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOnlinePartsOrderStatus(payload: any, orderId): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimate/orderLevel/statusUpdate/${orderId}`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncItemOrderToQBO(payload: any, orderId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/qbo/itemsOrder/sync/${orderId}`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  postSubletCartToQbo(payload: any, orderId: string) {
    return this.http
      .put(`${environment.api_base_url}/qbo/partItemsOrder/sync/${orderId}`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncPurchaseOrderToQBO(purchaseOrderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/qbo/po/sync/${purchaseOrderId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  syncPartsOrderToQBO(purchaseOrderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/qbo/partsOrder/sync/${purchaseOrderId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  updateManualOrderPaymentStatus(payload: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/manualOrder/paymentStatus/update`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateManualOrderOrderStatus(payload: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/manualOrder/orderStatus/update`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createQoute(orderId: string, vehicleId: string, isTriggerFromDialog?: boolean, data: { serviceId?: string; searchTerm?: string; partNumber?: string } = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/create/qoute`, {
      orderId,
      vehicleId,
      isTriggerFromDialog,
      ...data,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createTireHubQoute(orderId: string, vehicleId: string, isTriggerFromDialog?: boolean): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/tirehub/create/qoute?orderId=${orderId}&vehicleId=${vehicleId}&isTriggerFromDialog=${isTriggerFromDialog}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createNextpartQoute(data: {
    orderId: string;
    credentialId?: string;
    page?: string;
    isTriggerFromDialog?: boolean;
    openFrom?: string;
    serviceId?: string;
    searchTerm?: string;
    customerPrefferedLanguage?: string;
  }): Promise<any> {
    const customerPrefferedLanguage = localStorage.getItem('customerPreferredLanguage'); // Handle undefined case
    data.customerPrefferedLanguage = customerPrefferedLanguage;
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/nexpart/create/qoute`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncPartsTechQoute(orderId: string, isOverwrite: boolean = false): Promise<any> {
    const url = `${environment.api_base_url}/partstech/sync/qoute?orderId=${orderId}&isOverwrite=${isOverwrite}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  checkPartsTechAvailability(id: string, orderingType: string = 'repairOrder', data = { orderItemIds: [] }): Promise<any> {
    const payload = { id, orderingType };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/check/availability`, payload);

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  checkPartsTechItemAvailability({ orderItemId, quantity }): Promise<any> {
    const payload = { orderItemId, quantity };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/check/item/availability`, payload);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateNexPartItemCount({ orderId, orderItemId, quantity, itemName, oQuantity }): Promise<any> {
    const payload = { orderItemId, quantity, itemName, oQuantity };
    const url = `${environment.api_base_url}/nexpart/item/count/update/${orderId}`;

    return this.http.post(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updatePartsTechItemCount({ orderId, orderItemId, quantity, itemName, oQuantity }): Promise<any> {
    const payload = { orderItemId, quantity, itemName, oQuantity };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/item/count/update/${orderId}`, payload);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deletePartsTechItem(orderId: string, items: any[] = []): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partstech/item/delete/${orderId}`, { items }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireHubItemCount({ orderId, warehouseId, orderItemId, quantity, itemName, oQuantity }): Promise<any> {
    const payload = { warehouseId, orderItemId, quantity, itemName, oQuantity };
    const url = `${environment.api_base_url}/tireHub/cart/item/count/update/${orderId}`;

    return this.http.post(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteTireHubItem(orderId: string, items: any[] = []): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireHub/cart/item/delete/${orderId}`, { items }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireConnectItemCount({ orderId, warehouseId, orderItemId, quantity, itemName, oQuantity }): Promise<any> {
    const payload = { warehouseId, orderItemId, quantity, itemName, oQuantity };
    const url = `${environment.api_base_url}/tireConnect/cart/item/count/update/${orderId}`;

    return this.http.post(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteTireConnectItem(orderId: string, items: any[] = []): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireConnect/cart/item/delete/${orderId}`, { items }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateRepairLinkItemCount({ orderId, warehouseId, orderItemId, quantity, itemName, oQuantity }): Promise<any> {
    const payload = { warehouseId, orderItemId, quantity, itemName, oQuantity };
    const url = `${environment.api_base_url}/repairLink/cart/item/count/update/${orderId}`;

    return this.http.post(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteRepairLinkItem(orderId: string, items: any[] = []): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/repairLink/cart/item/delete/${orderId}`, { items }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkRepairLinkAvailability(orderId: string, data = { orderItemIds: [] }): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/repairlink/check/availability?orderId=${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkTireHubAvailability(orderId: string, data = { orderItemIds: [] }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/cart/availability/${orderId}`, { orderId });

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  checkTireConnectOnlineOrderAvailability(orderId: string, data = { orderItemIds: [] }): Promise<any> {
    const url = `${environment.api_base_url}/partsOrdering/inventory/availability/${orderId}`;

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  checkTireConnectAvailability(orderId: string, data = { orderItemIds: [] }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireConnect/cart/availability/${orderId}`, { orderId });

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOrderForPartialOrdering(orderId: string, data: { fetchInventory: boolean; partsOrderingType?: string } = { fetchInventory: false, partsOrderingType: '' }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/partial/ordering/order`, { orderId, ...data });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncNexpartQoute(orderId: string, isOverwrite: boolean = false): Promise<any> {
    const url = `${environment.api_base_url}/nexpart/cart/sync/${orderId}?isOverwrite=${isOverwrite}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncSpeedDialQoute(orderId: string, internalReferenceId: string, isOverwrite: boolean = false): Promise<any> {
    const url = `${environment.api_base_url}/speeddial/cart/sync/${orderId}?isOverwrite=${isOverwrite}&speedDialReferenceId=${internalReferenceId}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncRepairLinkQoute(orderId: string, isOverwrite: boolean = false): Promise<any> {
    const url = `${environment.api_base_url}/repairLink/cart/sync/${orderId}?isOverwrite=${isOverwrite}`;
    return this.http.put(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncTireHubQoute(orderId: string, isOverwrite: boolean = false): Promise<any> {
    const url = `${environment.api_base_url}/tirehub/sync/${orderId}?isOverwrite=${isOverwrite}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  syncTireConnect(orderId: string, isOverwrite: boolean = false): Promise<any> {
    const url = `${environment.api_base_url}/tireConnect/sync/${orderId}?isOverwrite=${isOverwrite}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  trackTireHubOrderDelivery(orderId: string, options: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/trackDelivery/${orderId}`, options);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getFreightCharges(data: any, options: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/frightCharges`, options);
    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTCFreightCharges(data: any, options: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tireConnect/frightCharges`, options);
    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  isSyncedWithPartsTech(orderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/partstech/sync/check?orderId=${orderId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartsTechOrder(orderId: string, forcePlaceOrder: boolean = false, isOverwrite: boolean = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/partstech/submit/order?orderId=${orderId}&forcePlaceOrder=${forcePlaceOrder}&isOverwrite=${isOverwrite}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartialNexPartOrder(orderId: string, data: any) : Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/nexpart/cart/customOrderPlace/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartialPartsTechOrder(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partstech/submit/partial/order?orderId=${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartialTireConnectInvOrder(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partsOrdering/inventory/placeOrder/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartialPartsTechInvOrder(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partstech/inventory/place/order?orderId=${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deliverPartialTireConnectInvOrder(moduleId: string, data = {}): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partsOrdering/inventory/markDelivered/${moduleId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deliverPartialPartsTechInvOrder(poId: string, data = {}): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partstech/inventory/deliver/order/${poId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartialTireHubOrder(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tirehub/cart/order/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placePartialTireConnectOrder(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireConnect/cart/order/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  placeRepairLinkOrder(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/repairLink/cart/order/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isPartstechConnected(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/partstech/isConnected`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isNexpartSellerCollected(data: { populateItemAndPricing?: boolean } = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/nexpart/sellers/connected`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  isMitchell1Connected(data: { populateItemAndPricing?: boolean; populateMitchell1Refs?: boolean } = { populateMitchell1Refs: false }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/mitchell/connected`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartsTechInitData(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/partstech/init/data`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isTireHubConnected(data: { populateItemAndPricing?: boolean; populateTireHubRefs?: boolean } = { populateTireHubRefs: false }): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/tirehub/isConnected`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isRepairLinkConnected(data: { populateItemAndPricing?: boolean; populateRepairLinkRefs?: boolean } = { populateRepairLinkRefs: false }): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/repairlink/isConnected`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isSpeedDialConnected(): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/speeddial/connected`, {}), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  speedDialConnect(data): Promise<any> {
    return this.http.post(`${environment.api_base_url}/speeddial/connect`, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getRepairLinkDetails(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/repairlink/details`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRepairLinkCart(orderId: string, sessionId: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/repairlink/cart/${orderId}?sessionId=${sessionId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSpeedDialCart(orderId: string, sessionId: string = ''): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/speeddial/cart/${orderId}?internalReferenceId=${sessionId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveRepairLinkCart(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/repairlink/cart/save/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateRepairLinkCart(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/repairlink/cart/update/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireHubCart(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireHub/cart/update/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireConnectCart(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tireConnect/cart/update/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSpeedDialCart(orderId: string, internalReferenceId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/speedDial/cart/update/${orderId}?internalReferenceId=${internalReferenceId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatePartsTechCart(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/partstech/cart/update/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateNexpartCart(orderId: string, data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/nexpart/cart/update/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPunchoutSessionDetails(orderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/repairlink/punchout/session/details/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isTireConnectConnected(data: { populateItemAndPricing?: boolean; populateTireConnectRefs?: boolean } = { populateTireConnectRefs: false }): Promise<any> {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/tireconnect/isConnected`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEstimates(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  generateOrderActivityLogFromClient(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/activity/create`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEstimatesForCustomer(data: any, token: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/approve/${data._id}`, this.helperService.encryptData(data), { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInvoiceSignature(data: any, token: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/invoice/sign/${data._id}`, this.helperService.encryptData(data), { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRecentAndTotalReviews(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/review/ratings`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRecentReviews(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/review/ratings`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReviewsRating(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/review/ratings/${data.startDate}/${data.endDate}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllReviewRating(data): Promise<any> {
    return (
      this.http
        // tslint:disable-next-line: max-line-length
        .get(`${environment.api_base_url}/review/pagination?type=${data.type}&limit=${data.limit}&skip=${data.skip}&sort=${data.sort || 'updatedAt'}&order=${data.order || -1}`, {
          headers: this.getHeaders(),
        })
        .toPromise()
        .then(this.successHandler.bind(this))
        .catch(this.errorHandler.bind(this))
    );
  }

  getReviewConfiguration(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/review/configuration`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPermission(data) {
    return this.http
      .post(`${environment.api_base_url}/permissions`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Create refund against a payment
   *
   * @param refundId - Refund id to update
   * @param data
   * @param data.amount - refund amount
   * @param data.refundDate - refund date
   * @returns
   */
  updateRefund(refundId: string, data) {
    return this.http
      .put(`${environment.api_base_url}/refunds/${refundId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createAdjustmentRefundAndFinalize(data: any) {
    return this.http
      .post(`${environment.api_base_url}/refunds/adjustments/finalize`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateItemLevelDelivery(orderId: string, data: any) {
    const url = `${environment.api_base_url}/estimate/item/deliver/update/${orderId}`;
    return this.http
      .put(url, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // paymentId is required in body
  payrixRefund(data: any) {
    return this.http
      .put(`${environment.api_base_url}/payrix/refund`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  payrixcheckStatus(paymentId) {
    return this.http
      .get(`${environment.api_base_url}/payrix/checkStatus/${paymentId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Get refund
   *
   * @param refundId - Refund id to update
   * @returns
   */
  getRefund(refundId: string) {
    return this.http
      .get(`${environment.api_base_url}/refunds/${refundId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Create refund against a payment
   *
   * @param data
   * @param data.paymentId - payment id to create refund against
   * @param data.payload - data payload for refund
   * @param data.payload.amount - refund amount
   * @param data.payload.refundDate - refund date
   * @returns
   */
  createRefund(data) {
    return this.http
      .post(`${environment.api_base_url}/refunds`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOrderInspection(data) {
    return this.http
      .put(`${environment.api_base_url}/estimateinspections/${data._id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatePOItemType(data) {
    return this.http
      .put(`${environment.api_base_url}/purchaseOrders/online/inventory/type`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderInspectionItems(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimateinspections/?estimateId=${data._id}&issueRaisedItems=true`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderInspectionItemsForCustomer(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimateInspectionsForCustomer/?estimateId=${data._id}&issueRaisedItems=true`, {
        headers: this.setHeaderToken(data.token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateReviewConfiguration(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/review/configuration`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendTestEmailReview(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/review/test`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  customerReviewFeedback(data: any, token: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/review/customerPreview`, this.helperService.encryptData(data), { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendReviewRequest(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/review/send`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Create Company*/
  createCompany(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/companies`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Update Company */
  updateCompany(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/companies/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Update Company */
  updateCompanyStatus(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/companies/update/liveAndDelete/${data.companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInvoiceCounter(data: any, companyId?: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/companies/update/invoiceCounter${companyId ? `/${companyId}` : ''}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Update Company Account Setting */
  updateAccountSettings(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/companies/account-setting`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Update Company by Super Admin*/
  updateCompanyBySuperAdmin(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/companies/admin/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* To check if company is unique or not */
  getCompanyIsUnique(companyName): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/companies/unique/name/${companyName}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  uploadDocument(model, data?: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/files?companyId=${data.companyId}`, this.helperService.encryptData(model), {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTemplates(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/templates`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAppointmentsByQuery(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appointments`, {
        ...data, // pagination , search and skip
      });

      return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));    
  }

  getAppointmentsByCustomerId(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appointments/customer/${data.customerId}?limit=${data.limit}&skip=${data.offset}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllAppointments(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appointments`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  verifyAppt(token, status) {
    return this.http
      .get(`${environment.api_base_url}/appointments/verify/${status}`, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDataForUnsubscribe(token: string) {
    return this.http
      .get(`${environment.api_base_url}/contacts/subscription/unsubcribe`, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  unsubscribeContact(token: string, data: any = {}) {
    return this.http
      .put(`${environment.api_base_url}/contacts/subscription/unsubcribe`, data, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  uploadCompanyLogo(file): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/upload/image`, file, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSuppliers(getList = true, activeStatus = 'all') {
    const body = {
      getList: getList,
      activeStatus: activeStatus,
    };
    return this.http
      .post(`${environment.api_base_url}/suppliers/getSupplier`, body, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getItemCategorys() {
    return this.http
      .get(`${environment.api_base_url}/items/category`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSupplierById(supplierId: string) {
    const body = {
      getList: true,
    };
    return this.http
      .post(`${environment.api_base_url}/suppliers/getSupplier/${supplierId}`, body, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getUniquePartsTechConstantsData() {
    return this.http
      .get(`${environment.api_base_url}/estimate/uniquePartsTechConstants`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLocationById(locationId: string) {
    return this.http
      .get(`${environment.api_base_url}/invLocations/${locationId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLocation(query: { activeOnly?: boolean, getBins?: boolean } = {}, list = false) {
    let url;
    if (list) {
      url = `${environment.api_base_url}/invLocations?list=${list}`;
    } else {
      url = `${environment.api_base_url}/invLocations?structure=flat&activeOnly=${query.activeOnly}`;
    }

    if (query?.getBins) {
      url += '&getBins=true';
    }
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getServiceCategories() {
    return this.http
      .get(`${environment.api_base_url}/services/categories`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCors(data: any): Promise<any> {
    const query = {
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
      coreRetrievalStatus: data.coreRetrievalStatus,
      returnType: data.returnType,
      itemType: data.itemType,
      coreReturnStatus: data.coreReturnStatus,
      supplier: data.supplier,
      returnStatus: data.returnStatus,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/adHocItems`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCompanyLocation(data: any): Promise<any> {
    const query = {
      limit: data.limit,
      skip: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
      search: data.search,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/list`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }
  updateCoreItemStatus(adHhocId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/adHocItems/coreReturn/status/${adHhocId}`, data, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateItemReturnStatus(adHhocId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/adHocItems/partReturn/status/${adHhocId}`, data, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLocations(data: any): Promise<any> {
    return this.http
      .get(
        `${environment.api_base_url}/invLocations?limit=${data.limit}&skip=${data.offset}&sort=${data.sortField}&order=${data.sortOrder}&search=${data.search}&activeStatus=${data.activeStatus}&structure=${data.structure}`,
        { headers: this.getHeaders() },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createWarehouse(data: any) {
    return this.http
      .post(`${environment.api_base_url}/warehouses`, data, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateWarehouse(warehouseId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/warehouses/${warehouseId}`, data, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createWarehouseByCompany(companyId: string, payload: any) {
    return this.http
      .post(`${environment.api_base_url}/warehouses/company/${companyId}`, payload, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateWarehouseByCompany(companyId: string, payload: any) {
    return this.http
      .put(`${environment.api_base_url}/warehouses/company/${companyId}/${payload.warehouseId}`, payload, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteWarehouseByCompany(companyId: string, warehouseId: string) {
    return this.http
      .delete(`${environment.api_base_url}/warehouses/company/${companyId}/${warehouseId}`, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createAisle(warehouseId: string, data: any) {
    return this.http
      .post(`${environment.api_base_url}/warehouses/aisle/${warehouseId}`, data, { headers: this.getFileHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateAisle(warehouseId: string, aisleId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/warehouses/aisle/${warehouseId}/${aisleId}`, data, { headers: this.getFileHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createInvLocation(data: any) {
    return this.http
      .post(`${environment.api_base_url}/invLocations`, data, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInvLocation(locationId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/invLocations/${locationId}`, data, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarehouses(fetchShopWarehouses: boolean = false) {
    let url = `warehouses`;
    if (fetchShopWarehouses) {
      url = `warehouses?fetchShopWarehouses=true`;
    }
    return this.http
      .get(`${environment.api_base_url}/${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarehouse(data: { fetchShopWarehouse?: boolean } = {}) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/warehouse/detail`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarehousesForCompany(companyId: string, search: string = '') {
    return this.http
      .get(`${environment.api_base_url}/warehouses/company/${companyId}?search=${encodeURIComponent(search)}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // For multilocation
  createShop(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/shop/create`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // to update multilocation
  updateShop(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/shop/update`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchInVehicleCarfax(data) {
    return this.http
      .post(`${environment.api_base_url}/vehicle/carfax/search`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSupplier(data) {
    return this.http
      .put(`${environment.api_base_url}/suppliers/${data._id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createSupplier(data) {
    return this.http
      .post(`${environment.api_base_url}/suppliers`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createItem(data: any) {
    return this.http
      .post(`${environment.api_base_url}/items`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getItemsByType(type?: string) {
    return this.http
      .get(`${environment.api_base_url}/items/type/${type}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getItemsNew(type?: string) {
    return this.http
      .get(`${environment.api_base_url}/items/type/${type}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getMultiplePricings(data: any[]) {
    return this.http
      .put(`${environment.api_base_url}/pricings/multiple`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchOrderItems(orderId: string, search: string = '', type: string = '') {
    return this.http
      .get(`${environment.api_base_url}/estimates/items/search/${orderId}?type=${type}&search=${encodeURIComponent(search)}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getItems(data: any, isForTransferOrder: boolean = false): Promise<any> {
    if (data.activeStatus !== 'all' && data.activeStatus !== true && data.activeStatus !== false) {
      data.activeStatus = 'all';
    } else {
      data.activeStatus = `${data.activeStatus}`;
    }

    if (data.stockStatus !== 'all' && data.stockStatus !== true && data.stockStatus !== false) {
      data.stockStatus = 'all';
    } else {
      data.stockStatus = `${data.stockStatus}`;
    }

    if (!['all', 'order', 'service'].includes(data.appliedStatus)) {
      data.appliedStatus = 'all';
    } else {
      data.appliedStatus = `${data.appliedStatus}`;
    }

    if (!['all', 'percentage', 'value'].includes(data.discountStatus)) {
      data.discountStatus = 'all';
    } else {
      data.discountStatus = `${data.discountStatus}`;
    }

    if (!['percent', 'fixed'].includes(data.feeValueType)) {
      data.feeValueType = 'all';
    }

    let url = `${environment.api_base_url}/items?`;
    url += `limit=${data.limit || 20}&`;
    url += `page=${data.offset || 1}&`;
    url += `sort=${data.sortField || ''}&`;
    url += `order=${data.sortOrder || ''}&`;
    url += `search=${data.search || ''}&`;
    url += `type=${data.type || ''}&`;
    url += `activeStatus=${data.activeStatus || ''}&`;
    url += `stocked=${data.stockStatus || ''}&`;
    url += `searchInMotor=${data.searchInMotor || ''}&`;
    url += `categoryId=${data.categoryId || ''}&`;
    url += `partAssociationSearch=${data.partAssociationSearch || ''}&`;
    url += `discountOn=${data.appliedStatus || ''}&`;
    url += `discountType=${data.discountStatus || ''}&`;
    url += `searchType=${data.searchType || ''}&`;
    url += `feeValueType=${data.feeValueType || ''}&`;

    if (data.estimateId) {
      url += `estimateId=${data.estimateId || ''}&`;
    }

    if (data.customerId) {
      url += `customerId=${data.customerId || ''}&`;
    }

    if (data.vehicleId) {
      url += `vehicleId=${data.vehicleId || ''}&`;
    }

    if (isForTransferOrder) {
      url += `isForTransferOrder=${isForTransferOrder}&`;
    }

    if (data?.fetchFETFee) {
      url += `fetchFETFee=${data.fetchFETFee}&`;
    }

    if (data?.fetchFET) {
      url += `fetchFET=${data.fetchFET}&`;
    }

    if (data?.isFromCatalouge) {
      url += `isFromCatalouge=${data.isFromCatalouge}&`;
    }

    if (data.motorQuery) {
      url += data.motorQuery;
    }

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTireStorage(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/items/tire/storage/list`, {
      ...data,
      page: data.offset,
      sort: data.sortField || '',
      order: data.sortOrder || '',
    });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartsLookupItems(): Promise<any> {
    const url = `${environment.api_base_url}/codeMaster/partLookItems`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getWarrantyPartsItems(): Promise<any> {
    const url = `${environment.api_base_url}/codeMaster/warrantyPartTypes`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCategoryByQuery(query: any, body: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/categories`, query);
    return this.http.put(url, body, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartLookupModalDataList(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/partLookup/${data.orderId}`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getStockLevelCost(itemId: string, warehouseId: string) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/items/getStockLevelCost`, { itemId, warehouseId });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getVehicleTireStorage({ customerId, vehicleId }) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/items/tire/storage/history`, { customerId, vehicleId });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getWarrantiesList(data: any): Promise<any> {
    data.limit = data.limit || 20;
    data.page = data.offset || 1;
    data.sort = data.sortField || '';
    data.order = data.sortOrder || '';
    data.sortOrder = data.sortOrder || '';
    data.search = data.search || '';

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/warranties`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSupplierList(data: any): Promise<any> {
    if (data.activeStatus !== 'all' && data.activeStatus !== true && data.activeStatus !== false) {
      data.activeStatus = 'all';
    } else {
      data.activeStatus = `${data.activeStatus}`;
    }
    data.limit = data.limit || 20;
    data.page = data.offset || 1;
    data.sort = data.sortField || '';
    data.order = data.sortOrder || '';
    data.sortOrder = data.sortOrder || '';
    data.search = data.search || '';
    data.supplierType = data.supplierType || '';
    data.activeStatus = data.activeStatus || '';
    data.startDate = data.storageStartDate || '';
    data.endDate = data.storageEndDate || '';
    data.sortField = data.sortField || '';
    data.getList = true;

    // const url = this.helperService.constructQueryParams(`${environment.api_base_url}/suppliers`, data);
    const url = `${environment.api_base_url}/suppliers/getSupplier`;

    //let url = `${environment.api_base_url}/suppliers?`;
    // url += `limit=${data.limit || 20}&`;
    // url += `page=${data.offset || 1}&`;
    // url += `sort=${data.sortField || ''}&`;
    // url += `order=${data.sortOrder || ''}&`;
    // url += `sortOrder=${data.sortOrder || ''}&`;
    // url += `search=${data.search || ''}&`;
    // url += `supplierType=${data.supplierType || ''}&`;
    // url += `activeStatus=${data.activeStatus || ''}&`;
    // url += `startDate=${data.storageStartDate || ''}&`;
    // url += `endDate=${data.storageEndDate || ''}&`;
    // url += `sortField=${data.sortField || ''}&`;
    // url += `getList=true`;

    // if (data.motorQuery) {
    //   url += data.motorQuery;
    // }

    return this.http.post(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getQBOSupplierList(): Promise<any> {
    let url = `${environment.api_base_url}/qbo/supplierList`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  addNewQBOSupplier(supplierId: any): Promise<any> {
    const url = `${environment.api_base_url}/qbo/createsupplier/${supplierId}`;
    return this.http.put(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getItemSoldReport(data: any): Promise<any> {
    const query: any = {
      // limit: data.limit || 15, here we commenting this line because in export csv we don't need limit
      limit: data.limit,
      page: data.skip / data.limit + 1 || 1,
      skip: data.skip || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      itemCategory: data.itemCategory || '',
      itemType: data.itemType || '',
      shopIds: data.shopIds,
      isPaidFull: data.isPaidFull,
      dateType: data.dateType || '',
      finalizeType: data.finalizeType || [],
      thirdPartyAccount: data.thirdPartyAccount || [],
      serviceWriter: data.serviceWriter || '',
      salesGroup: data.salesGroup || '',
      salesCode: data.salesCode || '',
      exportHybrid: data.exportHybrid || false,
      tech: data.tech || '',
      paymentStatus: data.paymentStatus || '',
      profitability: data.profitability || 0,
      updatedprofitability: data.updatedprofitability || 0,
    };
    if (!query.exportHybrid) {
      delete query.exportHybrid;
    }
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/suppliers/report`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAllLaborsSoldReport(data: any): Promise<any> {
    const query: any = {
      limit: data.limit || '',
      page: data.skip / data.limit + 1 || '',
      skip: data.skip || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      shopIds: data.shopIds,
      dateType: data.dateType || '',
      finalizeType: data.finalizeType || [],
      thirdPartyAccount: data.thirdPartyAccount || [],
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/labor`, query);
    return this.http.put(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTaxReportByItemType(data: any, type): Promise<any> {
    const url = `${environment.api_base_url}/reports/tax`;
    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTaxReportByItem(data: any): Promise<any> {
    const url = `${environment.api_base_url}/reports/tax`;
    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getLaborSoldReportById(data: any): Promise<any> {
    const query: any = {
      limit: data.limit || '',
      page: data.page || '',
      skip: data.skip || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      itemName: data.itemName || '',
      profitabilityUseWorkedHours: data.profitabilityUseWorkedHours || false,
      shopIds: data.shopIds,
      dateType: data.dateType || '',
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/labor/${data._id}`, query);
    return this.http.put(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAllDiscountsReport(data: any): Promise<any> {
    const body: any = {
      limit: data.limit,
      skip: data.skip,
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      discountOn: data.orderOn || '',
      discountType: data.orderDiscountType || '',
      shopIds: data.shopIds,
      finalizeType: data.finalizeType || [],
      thirdPartyAccount: data.thirdPartyAccount || [],
      dateType: data.dateType || '',
    };

    if (data.limit && !data.searchQuery) {
      body.page = data.skip / data.limit + 1;
    }

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/discount`, {});
    return this.http.put(url, body, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAllDiscountsReportById(data: any): Promise<any> {
    const query: any = {
      limit: data.limit,
      skip: data.skip,
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      itemName: data.itemName || '',
      profitabilityUseWorkedHours: data.profitabilityUseWorkedHours || false,
      discountOn: data.orderOn || '',
      discountType: data.orderDiscountType || '',
      shopIds: data.shopIds,
    };

    if (data.limit) {
      query.page = data.skip / data.limit + 1;
    }

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/discount/${data._id}`, query);
    return this.http.put(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getItemsReportSummaryById(data: any): Promise<any> {
    const query: any = {
      limit: data.limit,
      page: data.page,
      // skip: data.offset || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      supplier: data.supplier || '',
      type: data.type || '',
      shopIds: data.shopIds,
      dateType: data.dateType || '',
      serviceWriter: data.serviceWriter || '',
      salesGroup: data.salesGroup || '',
      salesCode: data.salesCode || '',
      finalizeType: data.finalizeType || [],
      thirdPartyAccount: data.thirdPartyAccount || [],
      tech: data.tech || '',
      paymentStatus: data.paymentStatus || '',
      // profitability: data.profitability || '',
      profitability: data.profitability || 0,
      updatedprofitability: data.updatedprofitability || 0,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/suppliers/report/${data._id}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  //  Fee all summary item by date and fee type
  getFeeSummaryReport(data: any): Promise<any> {
    const url = `${environment.api_base_url}/reports/fee`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  // Fee summary report by id
  getReportFeeItemName(data: any): Promise<any> {
    const url = `${environment.api_base_url}/reports/data/fee/name`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }
  // Fee summary report by id
  getFeeItemsReportSummaryById(data: any): Promise<any> {
    const url = `${environment.api_base_url}/reports/fee/${data.type}/${data.itemId}`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  // Fee discount report by id
  getDiscountsReportSummaryById(data: any): Promise<any> {
    const url = `${environment.api_base_url}/reports/discount/${data.discountOn}/${data.itemId}`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCommissionSummary(data: any) {
    const query: any = {
      limit: data.limit,
      page: data.page,
      sortField: data.sortField || '',
      sortOrder: data.sortOrder || '',
      search: data.searchQuery || '',
      paidDateStart: data.startDate || '',
      paidDateEnd: data.endDate || '',
      shopIds: data.shopIds || '',
      dateType: data.dateType || '',
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/commission/summary`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSelectedEmployeeDetail(employeeId: string) {
    const url = `${environment.api_base_url}/employees/selected/${employeeId}`;

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCommissionDrillDown(data: any) {
    const query: any = {
      limit: data.limit,
      page: data.page,
      sortField: data.sortField || '',
      sortOrder: data.sortOrder || '',
      search: data.searchQuery || '',
      paidDateStart: data.startDate || '',
      paidDateEnd: data.endDate || '',
      shopIds: data.shopIds || [],
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/commission/drilldown/${data.employeeId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getEmployeeTotalCommission(data: any) {
    const query: any = {
      paidDateStart: data.startDate || '',
      paidDateEnd: data.endDate || '',
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/commission/totalCommission/${data.employeeId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSupplierReportSummary(data: any): Promise<any> {
    const query: any = {
      limit: data.limit,
      page: data.page,
      // skip: data.offset || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      name: data.name || '',
      type: data.type || '',
      drilldown: data.drilldown || false,
      shopIds: data.shopIds,
      paymentTerms: data.paymentTerms,
      orderingMethod: data.orderingMethod,
      orderingStatus: data.orderingStatus,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/suppliers/summary`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSupplierReportSummaryList(data: any): Promise<any> {
    const query: any = {
      limit: data.limit,
      page: data.page,
      // skip: data.offset || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: encodeURIComponent(data.searchQuery || ''),
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      name: data.name || [],
      type: data.type || '',
      drilldown: data.drilldown || false,
      shopIds: data.shopIds,
      paymentTerms: data.paymentTerms,
      orderingMethod: data.orderingMethod,
      orderingStatus: data.orderingStatus,
      dateFillter: data.dateFillter,
    };

    const url = `${environment.api_base_url}/suppliers/summaryList`;
    return this.http.put(url, query, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSupplierReportSummaryById(data: any): Promise<any> {
    const query: any = {
      limit: data.limit,
      page: data.page,
      // skip: data.offset || '',
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: data.searchQuery || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      supplier: data.supplier || '',
      type: data.type || '',
      drilldown: data.drilldown || true,
      shopIds: data.shopIds,
      paymentTerms: data.paymentTerms,
      orderingMethods: data.orderingMethods,
      orderingStatus: data.orderingStatus,
      dateFillter: data.dateFillter,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/suppliers/summary/`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  removeItem(itemId): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/items/${itemId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  catalogSearchItem(queryData: any, type?: string, activeStatus: any = true, isFromCatalouge: boolean = false) {
      const url = this.helperService.constructQueryParams(`${environment.api_base_url}/items`, {
      ...queryData, // pagination , search and skip
      type,
      activeStatus,
      isFromCatalouge,
      order: queryData.sortOrder || '',
    });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  searchItem(queryString: string, type?: string, activeStatus: any = true, isFromCatalouge: boolean = false) {
    return this.http
      .get(`${environment.api_base_url}/items?search=${queryString}&type=${type}&activeStatus=${activeStatus}&isFromCatalouge=${isFromCatalouge}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncQboRefItems(trackDepartments = false) {
    return this.http
      .get(`${environment.api_base_url}/qboItems/qbo/sync?syncLocations=${trackDepartments}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncImportItems(companyId?: string) {
    return this.http
      .get(`${environment.api_base_url}/qbo/accounts/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise();
  }

  getItemsByCategory(type?: string, categoryId?: string) {
    return this.http
      .get(`${environment.api_base_url}/items/type/${type}/${categoryId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getItemById(itemId?: string, data: { populateSubItems: boolean } = { populateSubItems: false }) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/items/${itemId}`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getItemByIds(data: { itemIds: Array<any> } = { itemIds: [] }) {
    return this.http
      .post(`${environment.api_base_url}/items/get/byIds`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateItem(data: any, isUpdateCannedInspection: boolean = false) {
    return this.http
      .put(`${environment.api_base_url}/items/${data._id}?isUpdateCannedInspection=${isUpdateCannedInspection}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteItem(ItemId: string) {
    return this.http
      .delete(`${environment.api_base_url}/items/${ItemId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createInspection(data: any) {
    return this.http
      .post(`${environment.api_base_url}/inspections`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getInspection(fetchAssociatedCannedServices = false) {
    const enterprise = this.dataService.getEnterprise();
    let url = `${environment.api_base_url}/inspections`;
    if (fetchAssociatedCannedServices) url = `${environment.api_base_url}/inspections?fetchAssociatedCannedServices=true`;
    return this.http
      .get(url, {
        headers: this.getHeaders(),
        params: {
          enterprise,
        },
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addItemInInspections(payload) {
    return this.http
      .post(`${environment.api_base_url}/inspections/addItemInInspections`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addUpdateReturn(adHocItemId: string, payload: any) {
    return this.http
      .post(`${environment.api_base_url}/adHocItems/updateReturn${adHocItemId ? `/${adHocItemId}` : ''}`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveItemCoreCost(payload: any) {
    return this.http
      .post(`${environment.api_base_url}/adHocItems/saveItemCoreCost`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addUpdateOrderReturn(data: any) {
    return this.http
      .post(`${environment.api_base_url}/adHocItems/ordered/updateReturn`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addUpdateOrderedInventory(data: any) {
    return this.http
      .post(`${environment.api_base_url}/invStocks/ordered/inventory`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  removeItemInInspections(payload) {
    return this.http
      .post(`${environment.api_base_url}/inspections/removeItemInInspections`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCannedInspection(data) {
    const enterprise = this.dataService.getEnterprise() || 'autoleap';
    let url = `${environment.api_base_url}/inspections?`;
    if (data.limit) {
      url += `limit=${data.limit || ''}&`;
    }
    if (data.offset) {
      url += `page=${data.offset || ''}&`;
    }
    if (data.skip || data.skip == 0) {
      url += `skip=${data.skip || 0}&`;
    }
    if (data.sortField) {
      url += `sort=${data.sortField || ''}&`;
    }
    if (data.sortOrder) {
      url += `order=${data.sortOrder || ''}&`;
    }
    if (data.search) {
      url += `search=${data.search || ''}&`;
    }
    if (data.activeStatus) {
      url += `activeStatus=${data.activeStatus || ''}&`;
    }
    if (data.inspectionId) {
      url += `inspectionId=${data.inspectionId || ''}&`;
    }
    if (data.categoryId) {
      url += `categoryId=${data.categoryId || ''}`;
    }

    return this.http.get(url, { headers: this.getHeaders(), params: { enterprise } }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  searchInspection(search?: string) {
    const enterprise = this.dataService.getEnterprise();
    return this.http
      .get(`${environment.api_base_url}/inspections?search=${search || ''}`, {
        headers: this.getHeaders(),
        params: {
          enterprise,
        },
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getInspectionById(inspectionId: string) {
    return this.http
      .get(`${environment.api_base_url}/inspections/${inspectionId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTireStorageInspection(data) {
    return this.http
      .post(`${environment.api_base_url}/services/create/TireStorage/inspection`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInspection(data: any) {
    return this.http
      .put(`${environment.api_base_url}/inspections/${data._id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteInspection(ItemId: string) {
    return this.http
      .delete(`${environment.api_base_url}/inspections/${ItemId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteServiceInspection(data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/delete/serviceinspection`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveUpdateSubletMatrix(id: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/pricings/sublet?id=${id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSalesCode(data) {
    return this.http
      .get(`${environment.api_base_url}/codeMaster/salesCode?shortName=${encodeURIComponent(data)}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveSalesCode(data) {
    return this.http
      .post(`${environment.api_base_url}/categories/salesCode`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSalesCode(data: any) {
    return this.http
      .put(`${environment.api_base_url}/categories/salesCode`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  savePricingRules(data: any) {
    return this.http
      .put(`${environment.api_base_url}/pricings/rules`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  editPricingRules(id: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/pricings/rules?id=${id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPartstechSearch(partNumber) {
    return this.http
      .get(`${environment.api_base_url}/partstech/search/part/${partNumber}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireconnectSearch(data) {
    return this.http
      .post(`${environment.api_base_url}/tireconnect/inventory/byPartNumbers`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deletePricingRule(ruleId: any) {
    return this.http
      .delete(`${environment.api_base_url}/pricings/rules/remove/${ruleId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPricingCategories = (query?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getSearchPricingCategories = (query?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/search`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getPricingCategoriesById = (query?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getPricingSubCategories(id, type, query = '') {
    return this.http
      .get(`${environment.api_base_url}/codeMaster?categoryId=${id}&itemType=${type}&searchTerm=${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPricingSubSubCategories(categoryId, subCategoryId, type, query = '') {
    return this.http
      .get(`${environment.api_base_url}/codeMaster?categoryId=${categoryId}&subCategoryId=${subCategoryId}&itemType=${type}&searchTerm=${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  categorySearch(queryData: any, itemType: string = ''): Promise<any>  {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/list`, {
    ...queryData, // pagination , search and skip
    itemType,
  });

  return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
}

  getCategoryLevel = (query: any = {}) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/category/level/data`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getCategoryList = (query?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/list`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getSalesCodeList = (query?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/items/salesCode`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  // /company/app/order/royaltyfees

  royaltyFeesList = () => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/company/app/order/royaltyfees`, {});

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getPricings(type = 'part', itemType?) {
    return this.http
      .get(`${environment.api_base_url}/pricings?type=${type}${itemType ? `&itemType=${itemType}` : ''} `, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLaborPricingInitData() {
    return this.http
      .get(`${environment.api_base_url}/pricings/motor/init`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPricingsByCompany = (companyId, query?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/pricings/company/${companyId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  getQBOItemRefs(query: string = '', fetchItemStatus: boolean = false, offset?, pagination = false) {
    let url = `${environment.api_base_url}/qboItems?search=${query}&fetchItemStatus=${fetchItemStatus}`;
    if (pagination) url = `${environment.api_base_url}/qboItems/pagination?search=${query}&fetchItemStatus=${fetchItemStatus}&skip=${offset}&limit=200`;
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPricings(type = 'part', data) {
    return this.http
      .post(`${environment.api_base_url}/pricings?type=${type}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  // taxCodes/getTaxCodes
  getTaxCodeList(data = {}) {
    return this.http
      .post(`${environment.api_base_url}/taxCodes/getTaxCodes`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // /company/updateRoyaltyFees

  updateRoyaltyFee(data: any) {
    return this.http
      .put(`${environment.api_base_url}/company/updateRoyaltyFees`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  UpdatePricings(type = 'part', data) {
    return this.http
      .put(`${environment.api_base_url}/pricings/${data._id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createLaborPricings(data) {
    return this.http
      .post(`${environment.api_base_url}/pricings`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveLaborPricing(pricings: any[]) {
    return this.http
      .put(
        `${environment.api_base_url}/pricings/labor`,
        { pricings },
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveLaborPricingByCompany(companyId, pricings: any[]) {
    return this.http
      .put(
        `${environment.api_base_url}/pricings/company/${companyId}`,
        { pricings },
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setDefaultPartPricing(pricingId: string) {
    return this.http
      .put(
        `${environment.api_base_url}/pricings/part/default/${pricingId}`,
        {},
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateBulkPaymentMethodStatus(methods: any) {
    return this.http
      .put(`${environment.api_base_url}/paymentMethods/status`, methods, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  UpdateLaborPricings(data) {
    return this.http
      .put(`${environment.api_base_url}/pricings/${data._id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  removePricing(pricingId: any, type: string, isForceDelete: boolean = false): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/pricings/${pricingId}?type=${type}&isForceDelete=${isForceDelete}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  removePricingByCompany(companyId: any, pricingId: any, type: string, isForceDelete: boolean = false): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/pricings/company/${companyId}/${pricingId}?type=${type}&isForceDelete=${isForceDelete}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllCompanies() {
    return this.http
      .get(`${environment.api_base_url}/companies`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createCannedService(data) {
    return this.http
      .post(`${environment.api_base_url}/services`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCannedServices(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/services`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCannedServicesByItem(payload): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/services/updateItems`, payload, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCannedServicesByItem(items: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/services/getByItem`, {
      item: items,
    });
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }


  serviceSearch(queryData: any, activeStatus: any = 'all') {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/services`, {
    ...queryData, // pagination , search and skip
    activeStatus,
  });

  return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
}

  getServices(data: any): Promise<any> {
    const enterprise = this.dataService.getEnterprise();
    const query = {
      ...data,
      page: data.offset,
      sort: data.sortField,
      order: data.sortOrder,
    };

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/services`, query);

    return this.http.get(url, { headers: this.getHeaders(), params: { enterprise } }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCannedServiceById(data): Promise<any> {
    const enterprise = this.dataService.getEnterprise();
    return this.http
      .get(`${environment.api_base_url}/services/${data}`, {
        headers: this.getHeaders(),
        params: {
          enterprise,
        },
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  removeService(serviceId: any): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/services/${serviceId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateServiceById(data: any): Promise<any> {
    const enterprise = this.dataService.getEnterprise();
    return this.http
      .put(`${environment.api_base_url}/services/${data._id}`, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
        params: {
          enterprise,
        },
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  uploadCSVFile(formData, companyId, type: string = 'customer') {
    this.token = this.cookieService.getObject('alpat');
    return this.http
      .post(`${environment.api_base_url}/onboardings/upload/${type}/${companyId}`, this.helperService.encryptData(formData), {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  uploadAndImportCSV(formData, companyId, type: string = 'customer') {
    this.token = this.cookieService.getObject('alpat');
    return this.http
      .post(`${environment.api_base_url}/onboardings/upload/import/${type}/${companyId}`, this.helperService.encryptData(formData), {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  exportCSV(companyId: string, type: string = 'customer'): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/onboardings/export/${type}/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncGoogleReviews(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/review/google/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOnboardingStep(companyId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/companies/admin/onboarding/step/${companyId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  integrateTwilio(companyId: string, data: any) {
    return this.http
      .post(`${environment.api_base_url}/companies/integration/twilio/${companyId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  validateCSV(type: string, fileId: string, companyId: string) {
    return this.http
      .get(`${environment.api_base_url}/onboardings/validate/${companyId}/${type}/${fileId}`, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLatestOnboardingCSV(type: string, fileId: string, companyId: string) {
    return this.http
      .get(`${environment.api_base_url}/onboardings/latest/${type}/${companyId}`, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteCollection(type: string, companyId: string) {
    return this.http
      .delete(`${environment.api_base_url}/onboardings/delete-collection/${type}/${companyId}`, {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getUser(userId: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/users/${userId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  uploadAttachment(formData, type, id) {
    this.token = this.cookieService.getObject('alpat');
    return this.http
      .post(`${environment.api_base_url}/upload/${type}/${id}`, this.helperService.encryptData(formData), {
        headers: this.getFileHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  removeFile(imageType: string, shopId: string) {
    this.token = this.cookieService.getObject('alpat');

    const body = this.helperService.encryptData({ imageType });
  
    return this.http
      .delete(`${environment.api_base_url}/remove/${shopId}`, {
        body, 
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  
  getServerTime(token?) {
    return this.http
      .get(`${environment.api_base_url}/servertime`, {
        headers: token ? this.setHeaderToken(token) : this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  getCompanyPreferredLanguage() {
    return this.http
      .get(`${environment.api_base_url}/localization/getCompanyPreferredLanguage`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkCompanySite(companyId: string) {
    return this.http
      .get(`${environment.api_base_url}/company/check/site/${companyId}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendInspection(id = '', inspection) {
    return this.http
      .put(`${environment.api_base_url}/estimates/sendInspection/${id}`, this.helperService.encryptData(inspection), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendEstimation(id, estimate, isEnhancedInvoice = false) {
    return this.http
      .put(`${environment.api_base_url}/estimates/sendEstimate/${id}?isEnhancedInvoice=${isEnhancedInvoice}`, this.helperService.encryptData(estimate), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendEstimateDeposit(id, estimate) {
    return this.http
      .put(`${environment.api_base_url}/estimates/sendDepositRequest/${id}`, this.helperService.encryptData(estimate), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteEstimateDeposit(depositId: string): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/cancelDepositRequest/${depositId}`, this.helperService.encryptData({}), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendStatement(statementId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/statement/send/${statementId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendBulkStatement(data: any) {
    return this.http
      .post(`${environment.api_base_url}/statement/send/bulkstatement`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendBulkPaymentReceipt(paymentId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/bulkPayment/send/${paymentId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadEstimatePDF(id, type = 'pdf', isShowPaymentDetails = false, isInspectionReport = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/generatepdf/${id}?type=${type}&isShowPaymentDetails=${isShowPaymentDetails}&isInspectionReport=${isInspectionReport}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadCustomerPDF(id, type = 'pdf', isShowPaymentDetails = false, isInspectionReport = false, data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/customerGeneratepdf/${id}?type=${type}&isShowPaymentDetails=${isShowPaymentDetails}&isInspectionReport=${isInspectionReport}`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadCustomerEnhancedPDF(id, type = 'pdf', isEnhancedInvoice = false, isEnhancedEstimate = false, data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/customerGeneratepdf/${id}?type=${type}&isShowPaymentDetails=true&isEnhancedInvoice=${isEnhancedInvoice}&isEnhancedEstimate=${isEnhancedEstimate}`, {
        headers: this.setHeaderToken(data),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadEnhancedEstimatePDF(id, type = 'pdf', isShowPaymentDetails = false, isInspectionReport = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/generatepdf/${id}?type=${type}&isEnhancedEstimate=true&isShowPaymentDetails=${isShowPaymentDetails}&isInspectionReport=${isInspectionReport}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadEnhancedInvoicePDF(id, type = 'pdf', isShowPaymentDetails = false, isInspectionReport = false): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/generatepdf/${id}?type=${type}&isEnhancedInvoice=true&isShowPaymentDetails=${isShowPaymentDetails}&isInspectionReport=${isInspectionReport}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadTireStoragePdf(data: { orderId: string; tireStorageId: string }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/generate/pdf/tireStorage`, data);

    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  downloadTechWorksheetPDF(id: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/order/timesheet/pdf/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Create Employee*/
  createEmployee(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/employees`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /* Create Employee*/
  createUserByCompany(data: any, companyId: string): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/employees/company/${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  recordLoginActivity(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/loginActivity`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEmployeeDetails(id: string, data: { fetchInActive?: boolean; fetchShopDetails: boolean } = { fetchInActive: true, fetchShopDetails: false }) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/employees/${id}`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCompanyEmployeeByAdmin(companyId: string, userId: string) {
    return this.http
      .get(`${environment.api_base_url}/employees/company/${companyId}/${userId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCompanyEmployeeByAdmin(companyId: string, userId: string, data: any) {
    return this.http
      .put(
        `${environment.api_base_url}/employees/company/${companyId}/${userId}`,
        { data },
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEmployeeDetails(employee) {
    return this.http
      .put(`${environment.api_base_url}/employees/${employee._id}`, employee, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateStickyHeader(payload) {
    return this.http
      .put(`${environment.api_base_url}/employees/isServiceStickyHeader`, payload, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  isUniqueCompany(name, value, companyId) {
    return this.http.get(`${environment.api_base_url}/companies/unique/${companyId}?${name}=${value}`, {}).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  isUniqueEmployee(name, value) {
    return this.http
      .get(`${environment.api_base_url}/employees/unique?${name}=${value}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAppointmentsForTechs(techId) {
    return this.http
      .get(`${environment.api_base_url}/appointments/tech/${techId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchAppointmentForTech(techId: string, query: string) {
    return this.http
      .get(`${environment.api_base_url}/appointments/search/${query}?techId=${techId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimationsForTech(query?) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/tech`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkForUniqueInvCode(query: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/invoiceCode/unique`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkTechAssociation(orderId) {
    return this.http
      .get(`${environment.api_base_url}/estimates/isTechAssociated/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimationDetailForTech(orderId) {
    return this.http
      .get(`${environment.api_base_url}/estimates/tech/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  searchTechEstimates(techId: string, query: string) {
    return this.http
      .get(`${environment.api_base_url}/estimates?search=${query}&techId=${techId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  techTimeClockDetails() {
    return this.http
      .get(`${environment.api_base_url}/timesheets/tech/clock-status`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  techTimeByClockDetails(body) {
    return this.http
      .get(
        `${environment.api_base_url}/reports/tech/weeklyAttendaceLogs/${body._id}?startDate=${body.startDate}
      &endDate=${body.endDate}`,
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTechTimeAttendanceLogs(_id, startDate, endDate) {
    const query = `${_id}?startDate=${startDate}&endDate=${endDate}&order=1&sort=start`;
    return this.http
      .get(`${environment.api_base_url}/reports/tech/dailyAttendaceLogs/${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCommunications(entityId: string) {
    return this.http
      .get(`${environment.api_base_url}/communications?entityId=${entityId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  initUserAppData() {
    return this.http
      .get(`${environment.api_base_url}/init/user/app`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  initItemData(type: string = '', itemId: string = '') {
    return this.http
      .get(`${environment.api_base_url}/items/init/data/${itemId}?type=${type}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  serviceAction(orderId, data) {
    return this.http
      .put(`${environment.api_base_url}/order/service/timesheet/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInvoiceCodeManually(data: any) {
    return this.http
      .post(`${environment.api_base_url}/estimate/invoiceCode/update`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateClockStatus(data: any) {
    return this.http
      .post(`${environment.api_base_url}/timesheets/update/clock-status`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  inspectionAction(orderId, data) {
    return this.http
      .put(`${environment.api_base_url}/order/inspection/timesheet/${orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  changeTechPassword(data) {
    return this.http
      .post(`${environment.api_base_url}/changepassword`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEmployee(techId, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/employees/${techId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateUserProfile(techId, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/employees/company/${techId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createBulkPayment(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payments/statement/bulk`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createBulkCustomerPayments(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payments/customer/bulk`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPaymentPurchaseOrder(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payment/payable`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createBulkPayablePayment(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payments/payable/bulk`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatePaymentPurchaseOrder(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/payment/payable/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPaymetPurchaseOrder(purchaseOrderId: any, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/payment/purchaseOrder/${purchaseOrderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPayment(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payments`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  paymentSearch(query: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/payments?${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  paymentPurchaseOrder(id: string, isPurchaseOrder?, isTireConnectType?: boolean): Promise<any> {
    let url;
    if (!isPurchaseOrder) {
      if (isTireConnectType) {
        url = `${environment.api_base_url}/payments?moduleId=${id}`;
      } else {
        url = `${environment.api_base_url}/payments?purchaseOrderId=${id}`;
      }
    } else {
      url = `${environment.api_base_url}/payments?orderLevelPurchaseId=${id}`;
    }
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPayments(query: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/payments`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPaymentStatsReport(filter: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/payment/stats`, this.helperService.encryptData(filter), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPaymentReportList(filter: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/payment/list`, this.helperService.encryptData(filter), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  businessSummaryReport(filter: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/business/summary`, this.helperService.encryptData(filter), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateBusinessSummaryReport(filter: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/business/summary/updated`, this.helperService.encryptData(filter), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getUpdatedBusinessSummaryReportWithToken(token: string, filter): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/business/summary/updatedPreview`, this.helperService.encryptData(filter), {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getBusinessSummaryReportWithToken(token: string, filter): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/business/summary/preview`, this.helperService.encryptData(filter), {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  businessSummarySetInterval(data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/company/businessSummary/setInterVal`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  royaltyReportSetInterval(data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/company/royaltyReport/setInterVal`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteCategory(data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/tags/replaceAndDelete`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteCategoryById(codeId: any = {}, replaceCodeId: string = ''): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/codeMaster/category/delete/${codeId}/${replaceCodeId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  techLogoutCheck(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/timesheets/logout`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkForInprogressOrder(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/order/timesheet/inprogress`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  pauseAllOrders(): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/order/timesheet/pausesheet`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPaymentById(paymentId?: string) {
    return this.http
      .get(`${environment.api_base_url}/payments/${paymentId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerPayments(customerId: string, query: any = {}) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/payment/customer/${customerId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOutstandingInvoices(customerId: string, query: any = {}) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/outstanding/invoices/${customerId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPaymentHistoryByOrderId(orderId?: string) {
    return this.http
      .get(`${environment.api_base_url}/payments?orderId=${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getROBalanceHistoryByCustomerId(customerId?: string) {
    return this.http
      .get(`${environment.api_base_url}/customers/unpaid/estimates/${customerId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatePayment(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/payments/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteInspectionItem(inspectionId: string, categoryId: string, itemId: string) {
    return this.http
      .delete(`${environment.api_base_url}/estimateInspections/item/${inspectionId}?categoryId=${categoryId}&itemId=${itemId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateInspectionItem(inspectionItemId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimateInspections/item/${inspectionItemId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEstimateInspectionTech(techId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimateInspections/tech/${techId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateEstimateInspection(inspectionId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimateinspections/tech/${inspectionId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteEstimateInspection(inspectionId: string, estimateId: string, title: string) {
    return this.http
      .delete(`${environment.api_base_url}/estimateinspections/${inspectionId}?estimateId=${estimateId}&title=${title}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createEstimateInspection(data: any) {
    return this.http
      .post(`${environment.api_base_url}/estimateinspections`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createServiceInspection(data: any) {
    return this.http
      .post(`${environment.api_base_url}/estimateinspections/service/inspection`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createDVIServiceInspection(data: any) {
    return this.http
      .post(`${environment.api_base_url}/estimates/InspectionsService/${data.estimateId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createStorageInspection(data: any) {
    return this.http
      .post(`${environment.api_base_url}/estimateinspections/tireStorage/inspection`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateStorageInspection(data: any, id) {
    return this.http
      .put(`${environment.api_base_url}/estimateinspections/tireStorage/inspection/${id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerInspections(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimateinspections?customerId=${data.customerId}&limit=${data.limit}&skip=${data.offset}&sort=${data.sort || ''}&order=${data.order || ''}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateInspections(estimateId: any) {
    return this.http
      .get(`${environment.api_base_url}/estimateinspections?estimateId=${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateInspectionDetails(inspectionId: string, estimateId: string, populateEstimate: boolean = false, populateCustomer: boolean = false) {
    return this.http
      .get(`${environment.api_base_url}/estimateinspections/${inspectionId}?estimateId=${estimateId}&populateEstimate=${populateEstimate}&populateCustomer=${populateCustomer}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createActivityLog(data) {
    return this.http
      .post(`${environment.api_base_url}/appLogs/${data.orderId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createOrderActivity(orderId: string, activity: any) {
    return this.http
      .post(`${environment.api_base_url}/appLogs/order/activity/${orderId}`, activity, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getActivityLog(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAppLogsByQuery(query?) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs/getByQuery`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getEstimatePayments(estimateId: string) {
    return this.http
      .get(`${environment.api_base_url}/payments?estimateId=${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimatePaymentDetails(estimateId) {
    return this.http
      .get(`${environment.api_base_url}/estimates/${estimateId}?type=payment`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrders() {
    return this.http
      .get(`${environment.api_base_url}/estimates/kanban/`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getKanbanInvoicePaidSettings() {
    return this.http
      .get(`${environment.api_base_url}/kanbans/invoicepaidsettings`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  undoOrderStatus(id) {
    return this.http
      .get(`${environment.api_base_url}/kanbans/undoorderstatus/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getKanbans(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/kanbans`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getKanbans2(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/kanbans/filter/orders`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getKanbanColumn(id: any) {
    return this.http
      .get(`${environment.api_base_url}/kanbans/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSingleOrder(id: any) {
    return this.http
      .get(`${environment.api_base_url}/kanbans/singleOrder/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createKanbanColumn(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/column/add`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderFlagsCount() {
    return this.http
      .get(`${environment.api_base_url}/kanbans/count/orders`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTechFlag(orderId: string, serviceId: string, data, role: string) {
    return this.http
      .put(`${environment.api_base_url}/estimates/techflag/update/${orderId}/${serviceId}?role=${role}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCommissionSettings(data) {
    return this.http
      .put(`${environment.api_base_url}/company/updateCommissionable`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateOfficeFlag(orderId: string, serviceId: string, data, role: string) {
    return this.http
      .put(`${environment.api_base_url}/estimates/officeflag/update/${orderId}/${serviceId}?role=${role}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderService(orderId: string, serviceId: string, populateInspection = false, populateAdHocs = false, populateTireStorage = false, populateQoutes = false) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/service/${orderId}/${serviceId}`, {
      populateInspection,
      populateAdHocs,
      populateTireStorage,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOrderServices(orderId: string, populateInspection = false, populateAdHocs = false, populateTireStorage = false, populateQoutes = false) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/service/${orderId}`, {
      populateInspection,
      populateAdHocs,
      populateTireStorage,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  saveServiceItem(orderId: string, serviceId: string, itemId: string, data) {
    return this.http
      .put(`${environment.api_base_url}/estimates/tire/dot/update/${orderId}/${serviceId}/${itemId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveBulkServiceItemDotNumbers(orderId: string, serviceId: string, data) {
    return this.http
      .put(`${environment.api_base_url}/estimates/tire/dot/multiple/update/${orderId}/${serviceId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateKanbanColumn(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  rearrangeKanbanBoard(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/reArrangeKanban`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  reOrderWorkboardCard(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/reOrderWorkboardCard`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateWorkboardColumnOrder(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/updateWorkboardColumnOrder`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  changeWorkboardOrderWorkflow(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/changeWorkboardOrderWorkflow`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteKanbanColumn(id: any) {
    return this.http
      .delete(`${environment.api_base_url}/kanbans/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  arvhiveKanbanColumn(data: any) {
    return this.http
      .put(`${environment.api_base_url}/kanbans/archiveOrders/${data._id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  duplicateTicket(orderId) {
    return this.http
      .get(`${environment.api_base_url}/estimates/kanban/duplicateOrder/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWorkbanFlow() {
    return this.http
      .get(`${environment.api_base_url}/kanbans/status`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  archiveTicket(orderId) {
    return this.http
      .put(
        `${environment.api_base_url}/estimates/kanban/archiveOrder/${orderId}`,
        {},
        {
          headers: this.getHeaders(),
        },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  unArchiveOrder(orderId: string) {
    return this.http
      .put(`${environment.api_base_url}/estimates/kanban/unArchiveOrder/${orderId}`, {}, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteEstimate(estimateId) {
    return this.http
      .delete(`${environment.api_base_url}/estimates/${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getNotifications(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appLogs/notifications?limit=${data.limit}&skip=${data.offset}&sort=${data.sortField}&order=${data.sortOrder}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTechNotifications(limit: number, skip: number, sortBy = 'createdAt', sortOrder = -1) {
    return this.http
      .get(`${environment.api_base_url}/appLogs?type=notification&limit=${limit}&skip=${skip}&sort=${sortBy}&order=${sortOrder}&forTech=true`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  UpdateNotificationsStatus(data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/appLogs/markRead`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  dismissNotifications(item, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/appLogs/markAsDismissed/${item._id}?priority=${item.priority}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  dismissAllNotifications(id, data): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/appLogs/markAsDismissed/${id}?priority=${data.priority}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  markAllReadNotifications(techApp = false): Promise<any> {
    const bodyParams = techApp ? { forTech: true } : null;
    return this.http
      .put(`${environment.api_base_url}/appLogs/markAllRead`, bodyParams, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectQBO(companyId: string) {
    return this.http
      .get(`${environment.api_base_url}/qbo/connect/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkGoogleConnection() {
    return this.http
      .get(`${environment.api_base_url}/review/google/status`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkApptGoogleConnection() {
    return this.http
      .get(`${environment.api_base_url}/appointmentRequests/googleBookingStatus`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setGoogleBookOnline(state: boolean) {
    return this.http
      .get(`${environment.api_base_url}/appointmentRequests/googleBookingInit?activationState=${state}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  listGroupsAndBusiness() {
    return this.http
      .get(`${environment.api_base_url}/review/google/listGroupsAndBusiness`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  sendGroupAndBusiness(group: any, business: any, address: any) {
    return this.http
      .put(`${environment.api_base_url}/review/google/setGroupAndBusiness`, { group, business, address }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  connectToGoogle() {
    return this.http
      .get(`${environment.api_base_url}/review/google/connect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  googleReviewResponse(reviewId: string, comment: string) {
    return this.http
      .put(`${environment.api_base_url}/review/google/reply/${reviewId}`, { comment }, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectGoogle() {
    return this.http
      .get(`${environment.api_base_url}/review/google/disconnect`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncInvoice() {
    return this.http
      .get(`${environment.api_base_url}/invoice/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncCompleteInvoiceInQBO(invoiceId: string, options: { payment?: string } = {}) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invoice/qbo/sync/${invoiceId}`, options);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  importPayment() {
    return this.http
      .get(`${environment.api_base_url}/paymentmethods/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  disconnectQBO(companyId) {
    return this.http
      .get(
        // `${config.localServerUrl}/qbo/connect`,
        `${environment.api_base_url}/qbo/disconnect/${companyId}`,
        { headers: this.getHeaders() },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBOCompanyDetails(data: { fetchQuickBooksCompany: boolean } = { fetchQuickBooksCompany: true }) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/qbo/company/info`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBOCustomers(companyId, fileType = 'json') {
    return this.http
      .get(`${environment.api_base_url}/qbo/customers/${companyId}?type=${fileType}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBOChartAccounts(companyId) {
    return this.http
      .get(`${environment.api_base_url}/qbo/accounts/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBOPaymentMethods(companyId) {
    return this.http
      .get(`${environment.api_base_url}/qbo/paymentmethods/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBOTaxes(companyId) {
    return this.http
      .get(`${environment.api_base_url}/qbo/taxcodes/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncQBOTaxes(companyId: string = '') {
    return this.http
      .get(`${environment.api_base_url}/taxcodes/qbo/sync?companyId=${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncQBOPaymentMethods() {
    return this.http
      .get(`${environment.api_base_url}/paymentmethods/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncPaymentExpenditureQBO() {
    return this.http
      .get(`${environment.api_base_url}/paymentmethodsExpenditure/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncQBOCustomers() {
    return this.http
      .get(`${environment.api_base_url}/customers/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncQBOItems() {
    return this.http
      .get(`${environment.api_base_url}/items/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncEstimates() {
    return this.http
      .get(`${environment.api_base_url}/invoice/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncPayments() {
    return this.http
      .get(`${environment.api_base_url}/payment/qbo/sync`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createQBOTaxes(data) {
    return this.http
      .post(`${environment.api_base_url}/taxCodes`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createTaxesByCompany(data: any, companyId: string) {
    return this.http
      .post(`${environment.api_base_url}/taxCodes/company/${companyId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveTaxSettings(data: any, companyId: string) {
    return this.http
      .put(`${environment.api_base_url}/taxCode/settings?companyId=${companyId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBOItems(companyId, fileType = 'json') {
    return this.http
      .get(`${environment.api_base_url}/qbo/items/${companyId}?type=${fileType}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBORefItems(companyId) {
    return this.http.get(`${environment.api_base_url}/qbo/qboItems/${companyId}`, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  searchEmpolyees(query: string, data) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/employees/`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  searchCustomer(query: string, data: CustomerSearch = {}) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers/search/${encodeURIComponent(query)}`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  globalSearch(query: string, data: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/globalsearch/${query}`, data);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  globalSearchEnhanced(config: any, data: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/globalsearch`, config);
    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTaxCodes(query: { type?: string } = {}) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/taxCodes`, query), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTax(taxId: string, taxType: string, companyId: string = '') {
    return this.http
      .get(`${environment.api_base_url}/tax/${taxType}/${taxId}?companyId=${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTaxesData(companyId: string = '') {
    const url = `${environment.api_base_url}/taxCodes/taxes/data${companyId ? `/${companyId}` : ''}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCompanyTaxes(companyId: string) {
    return this.http
      .get(`${environment.api_base_url}/taxCodes/company/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setTaxCodeDefault(id) {
    return this.http
      .get(`${environment.api_base_url}/taxCodes/default/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setTaxCodeDefaultByAdmin(companyId: string, taxId: string) {
    return this.http
      .get(`${environment.api_base_url}/taxCodes/admin/default/${companyId}/${taxId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPaymentMethods(): Promise<getQboPaymentMethods[]> {
    return this.http
      .get<getQboPaymentMethods[]>(`${environment.api_base_url}/paymentMethods`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCommissionPlans(planId?: string) {
    return this.http
      .get(`${environment.api_base_url}/company/commissionPlans${planId ? '/' + planId : ''}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDeletdROs(data: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/deleted/order`, data);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrderSettlementDetails(orderId: string, query = { fetchPayMethods: false }) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimates/settlement/details/${orderId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getActivityLogCommissionPlans(planId?: string) {
    // let url = '/appLogs/commissionPlans' + planId ? '/' + planId : '';
    return this.http
      .get(`${environment.api_base_url}/appLogs/getByType/commissionPlans${planId ? '/' + planId : ''}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addUpdateCommissionPlan(data: any) {
    return this.http
      .post(`${environment.api_base_url}/company/commissionPlans`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  finalizeOrder(orderId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimates/finalize/order/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addUpdateMarkRoThirdParty(data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimate/markROThirdParty`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateMarkAsEmployeeSales(data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimate/markROEmployeeSale`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getActivePaymentMethods(type?) {
    let url;
    if (type) {
      url = '/activepm?integrationType=accPayable';
    } else {
      url = '/activepm';
    }

    return this.http
      .get(`${environment.api_base_url}${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setPaymentMethodDefault(id, status) {
    return this.http
      .get(`${environment.api_base_url}/paymentMethods/status/${id}/${status}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPartTechBrandsList(data) {
    const query: any = {
      limit: data.limit,
      skip: data.offset,
      searchTerm: data.searchQuery || '',
      status: data.status,
    };

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/brand/list`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartWarrantiesBrands(data) {
    const query: any = {
      limit: data.limit,
      skip: data.offset,
      searchTerm: data.searchQuery || '',
      itemType: data.itemType,
      codeType: data.codeType,
    };

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/brands`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartWarantiesSubBrandsList(data) {
    const query: any = {
      limit: data.limit,
      skip: data.offset,
      searchTerm: data.searchQuery || '',
      itemType: data.itemType,
      codeType: data.codeType,
      categoryId: data.categoryId,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/brands`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  deleteSubBrand(categoryId: string, subCategoryId: string, itemType: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/codeMaster/brand/subCategory?itemType=${itemType}&categoryId=${categoryId}&subCategoryId=${subCategoryId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSubBrand(data: any) {
    return this.http
      .put(`${environment.api_base_url}/codeMaster/brand/subCategory`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createSubBrand(data: any) {
    return this.http
      .post(`${environment.api_base_url}/codeMaster/brand/subCategory`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateWarranty(data: any, warrantyId: string) {
    return this.http
      .put(`${environment.api_base_url}/warranties/${warrantyId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteWarrantyById(warrantyId: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/warranties/${warrantyId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createWarranty(data: any) {
    return this.http
      .post(`${environment.api_base_url}/warranties`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPartTechSupplierList(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/partstech/supplier/list`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  assignTechToInspection(inspectionId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimateInspections/assignTech/${inspectionId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTaxCode(data: any, companyId: string = '') {
    return this.http
      .put(`${environment.api_base_url}/tax/code?companyId=${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateServiceInspection(inspectionId: string, data: any) {
    return this.http
      .put(`${environment.api_base_url}/estimateInspections/service/inspection/${inspectionId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEstimateApppointments(estimateId: string) {
    return this.http
      .get(`${environment.api_base_url}/appointments/estimates/${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCustomerEstimates(customerId: string) {
    return this.http
      .get(`${environment.api_base_url}/customers/estimates/${customerId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getChartOfAccounts() {
    return this.http
      .get(`${environment.api_base_url}/chartOfAccounts`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAdminChartOfAccounts(companyId) {
    return this.http
      .get(`${environment.api_base_url}/admin/chartOfAccounts/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setAdminChartOfAccountDefault(acc: any, companyId) {
    return this.http
      .get(`${environment.api_base_url}/admin/chartOfAccounts/default/${acc._id}/${acc.classification}/${companyId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  setChartOfAccountsDefault(acc: any) {
    return this.http
      .get(`${environment.api_base_url}/chartOfAccounts/default/${acc._id}/${acc.classification}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getQBoActionItemsStatus(id) {
    return this.http
      .get(`${environment.api_base_url}/qbo/onboarder/logs/${id}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getActivityLogs(data) {
    return this.http
      .get(`${environment.api_base_url}/appLogs?limit=${data.limit}&skip=${data.offset}&sort=${data.sortField}&order=${data.sortOrder}&search=${data.search}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getApplogCount(query?: string) {
    return this.http
      .get(`${environment.api_base_url}/appLogs/notification/unread?${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  markApplogAsRead(data: any) {
    return this.http
      .put(`${environment.api_base_url}/appLogs/markRead`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getKeyIndicators(data: any) {
    return this.http
      .put(`${environment.api_base_url}/dashboard/keyIndicators`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSalesTrend(data: any) {
    return this.http
      .put(`${environment.api_base_url}/dashboard/salesTrend`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReviewSurveys(data: any) {
    return this.http
      .put(`${environment.api_base_url}/dashboard/reviewSurveys`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getProfitability(data: any) {
    return this.http
      .put(`${environment.api_base_url}/dashboard/profitability`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getInvoiceAging(data: any) {
    return this.http
      .put(`${environment.api_base_url}/dashboard/invoiceAging`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  durationUpdate() {
    return this.http
      .get(`${environment.api_base_url}/dashboard/durationUpdate`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportSalestrend(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/salesTrend`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getSalesTrendHistory(id: string, type: string) {
    return this.http
      .get(`${environment.api_base_url}/invStocks/salesTrend/${type}/${id}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportSalesTrendList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/salesTrendList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportSalesTrendListExport(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/salesTrendListExport`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportCannedservice(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/cannedService`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportCannedServiceList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/cannedServiceGrList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportCannedServiceDetailList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/cannedServiceDetailsList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportOutstandingInvoice(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/invoiceOutstanding`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPlistSupplier(data: any) {
    return this.http
      .put(`${environment.api_base_url}/suppliers/report/accountsPayableList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPlistSupplierDrillDown(query: any, data: any) {
    const url = `${environment.api_base_url}/suppliers/report/accountsPayableListDrillDown/${data._id}`;
    return this.http.put(url, query, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getInvoiceOutstandingAggregate(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/invoiceOutstandingAggregate`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportOutstandingInvoiceList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/invoiceOutstandingList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAccountsReceivable(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/accountsReceivableKPI`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAccountsReceivableList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/accountsReceivableList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAccountsReceivableHistory(data: any) {
    return this.http
      .get(`${environment.api_base_url}/customers/creditHistory/${data.customerId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRoyaltyFeeSalesBreakDown(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/royaltyfee/salesBreakDown`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRoyaltyFeeKPI(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/royaltyfee/kpis`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  royaltyFeeXMLExport(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/exportRoyaltyfee/xml`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getFeeSalesBreakDownWithToken(token: string, filter): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/royaltyfee/salesBreakDown`, this.helperService.encryptData(filter), {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRoyaltyReportFeeKPIWithToken(token: string, filter): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/reports/royaltyfee/kpis`, this.helperService.encryptData(filter), {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getObjectId() {
    return this.http.get(`${environment.api_base_url}/getObjectId`, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getReportArMovement(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/arMovement`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportArMovementStats(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/apMovementStats`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPMovementSales(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/apMovementSale`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPMovementPurchases(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/apMovementPurchase`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPMovementReturns(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/apMovementReturn`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPMovementAdjustments(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/apMovementAdjustment`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportAPMovementStockTransfer(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/apMovementStockTransfer`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportTechSummaryByAllService(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getTechWithoutActualSummary`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportTechSummary(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getTechSummary`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportTechSummaryList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getTechSummaryList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportTechDetails(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getTechDetails`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRevenueRecognitionPaymentMethods(query: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/activepm`, query);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTechnicianDrillDown(query: any, data: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/techDrillDown`, query);

    return this.http
      .put(url, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTechnicianByAllServiceDrillDown(query: any, data: any) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/withoutActualTechDrillDown`, query);

    return this.http
      .put(url, this.helperService.encryptData(data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportTechTimeLog(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getTechTimeLog`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportTechTimeLogList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getTechTimeLogList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportOrders(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getOrders`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOrdersAggregate(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getOrdersAggregate`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  getReportCustomer(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getCustomer`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportCustomerList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getCustomerList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportGoogleReview(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getGoogleReview`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportGoogleReviewList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getGoogleReviewList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportInternalReview(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getInternalReview`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReportInternalReviewList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/getInternalReviewList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTaxReportStats(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/taxes/stats`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTaxReportList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/taxes/list`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTaxRateReportList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/taxes/rate/list`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCommissionDetails(orderId: string, employeeId: string = '', serviceId: string = '', employeeRole: string = '') {
    let url = `${environment.api_base_url}/commissionDetails/${orderId}`;

    if (employeeId) {
      url += `/${employeeId}`;
    }

    return this.http
      .get(this.helperService.constructQueryParams(url, { serviceId, employeeRole }), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  syncCommissionDetails(orderId: string) {
    let url = `${environment.api_base_url}/commissionDetails/sync/${orderId}`;

    return this.http.put(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateCommissionDetails(employeeId: string, orderId: string, data: any) {
    let url = `${environment.api_base_url}/commissionDetails/update/${orderId}/${employeeId}`;

    return this.http.put(url, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  appLogsSearch(query): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appLogs?${query}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  appQBORepostListById(itemId): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appLogs/${itemId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  filterLogs(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/appLogs/filtered/logs`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  goLive(companyId: string, data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/companies/golive/${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDocument(entityId, token = null) {
    return this.http
      .get(
        // `${config.localServerUrl}/qbo/connect`,
        `${environment.api_base_url}/documents/${entityId}`,
        { headers: token ? this.setHeaderToken(token) : this.getHeaders() },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDocumentByCustomer(entityId, token = null) {
    return this.http
      .get(`${environment.api_base_url}/documents/byCustomer/${entityId}`, { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getOwnShop() {
    return this.http.get(`${environment.api_base_url}/shop`, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getServiceNotes(estimateId: string, serviceId: string, isOrderHistory: boolean = false) {
    return this.http
      .get(
        // `${config.localServerUrl}/qbo/connect`,
        `${environment.api_base_url}/estimates/updateNotes/${estimateId}/${serviceId}?isOrderHistory=${isOrderHistory}`,
        { headers: this.getHeaders() },
      )
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateServiceNotes(estimateId, serviceId, data) {
    return this.http
      .post(`${environment.api_base_url}/estimates/updateNotes/${estimateId}/${serviceId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateServiceAuthStatus(estimateId: string, data: any) {
    return this.http
      .post(`${environment.api_base_url}/estimates/service/approve/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Update app state for current user
   * @param statePayload state payload object
   * @param statePayload.module module name
   * @param statePayload.type type of state
   * @param statePayload.name name of field
   * @param statePayload.payload state payload
   */
  updateAppState(statePayload) {
    return this.http
      .post(`${environment.api_base_url}/appStates`, this.helperService.encryptData(statePayload), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * Get app state for current user
   * @param query state query object
   * @param query.module module name
   * @param query.type type of state
   * @param query.name name of field
   */
  getAppState(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appStates`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOnboarders(userId, query?) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/onboarders${userId ? `/${userId}` : ''}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  isUniqueOnboarder(keyName, keyValue) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/onboarders/unique`, { [keyName]: keyValue });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createOnboarder(payload) {
    const url = `${environment.api_base_url}/onboarders`;
    return this.http.post(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateOnboarder(userId, payload) {
    const url = `${environment.api_base_url}/onboarders/${userId}`;
    return this.http.put(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  selfSignup(token, payload) {
    const url = `${environment.api_base_url}/companies/selfSignup`;
    return this.http
      .post(url, payload, {
        headers: this.setHeaderToken(token),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateItemLocationFromPO(itemId, payload) {
    const url = `${environment.api_base_url}/items/updateLocationFromPO/${itemId}`;
    return this.http.put(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getProfitabilityStats(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/profitabilityStats`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getProfitabilityList(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/profitabilityList`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getActivityLogsByCompanyId(companyId: string, query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs/company/${companyId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getActivityByEntityType(moduleId: string, entityType: string, qouteId: string) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs/entity/${moduleId}/${entityType}/${qouteId}`, {});
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerCenterBasicDetails(customerId: string) {
    const url = `${environment.api_base_url}/customers/customer-center/basic/${customerId}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerCenterInsights(customerId: string, shopIds: string[]) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/customers/customer-center/insights/${customerId}`, { shopIds });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerStatementStats(customerId: string) {
    const url = `${environment.api_base_url}/customers/customer-center/insights/statements/${customerId}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerCenterHistory(customerId: string, query: string = '') {
    const url = `${environment.api_base_url}/customers/customer-center/history/${customerId}?search=${query}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCsutomerSalesTrends(customerId: string) {
    const url = `${environment.api_base_url}/customers/customer-center/sales-trend/${customerId}`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomerCompanyNames() {
    const url = `${environment.api_base_url}/customers/companyNames`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getEstimateProfitability(orderId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/estimates/profitability/${orderId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAttendanceLogs(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/attendaceLogs`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAttendanceLogDetailsList(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/detailedAttendaceLogList`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getTechServicesById(techId: string) {
    return this.http
      .get(`${environment.api_base_url}/reports/tech/services/${techId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  detectTimeOverlap(techId: string, query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/tech/detectTimeOverlap/${techId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  createTechService(tehcId: string, query): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/tech/addTechTimelog/${tehcId}`, query);

    return this.http.post(url, {}, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateTechAttendence(tehcId: string, payload) {
    const url = `${environment.api_base_url}/reports/tech/updateTechTimelog/${tehcId}`;
    return this.http.post(url, payload, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getAttendanceLogDetailsStats(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/detailedAttendaceLogStats`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getInventoryLogStats(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invLogs/stats`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getInventoryLogs(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invLogs`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getInventoryLevelReport(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invStocks/report/inventoryLevel`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCashDrawerList(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/cashDrawer/list`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCashDrawerListDetails(recordId?: string, query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/cashDrawer/list/${recordId}`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartsTechPartsOrder(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/partsTechPartsOrder`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getOrderUnusedItem(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/ordered/unused`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getPartsOrderingOrder(orderId: string, itemSource: string, cartId: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/ordered/cart/${orderId}`, { itemSource, cartId });

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getRolePrivileges(query?: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/roles`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  extenTrialByOnboarder(companyId: string, data: any) {
    return this.http
      .post(`${environment.api_base_url}/companies/guidedTrials/extend/${companyId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  markTrialStep(stepNames: string[]) {
    return this.http
      .put(`${environment.api_base_url}/companies/markTrialStep`, this.helperService.encryptData({ stepNames }), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getDeferredServices(data: any): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/services/deferredService`, data);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getMarketingGroup(marketingGroupId: string, data: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingGroup/${marketingGroupId}`, {
      limit: data.limit,
      skip: data.offset,
      sortField: data.sortField,
      sortOrder: data.sortOrder,
      searchQuery: data.searchQuery,
      groupId: data.groupId,
    });
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  saveMarketingGroup(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingGroup`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateMarketingGroup(marketingGroupId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/marketingGroup/${marketingGroupId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getLeadSource(list: boolean, data: any = {}): Promise<any> {
    let queryParams;
    if (list) {
      queryParams = { list: list };
    } else {
      queryParams = {
        limit: data.limit,
        skip: data.offset,
        sort: data.sortField,
        order: data.sortOrder,
        list: list,
        search: data.searchQuery,
        isActive: data.isActive,
      };
    }
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/leadCampaign/`, queryParams);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  saveLeadSource(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/marketingCampaign/leadCampaign`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateLeadSource(leadId: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/marketingCampaign/leadCampaign/${leadId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCampaignCategory(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/multiple/tags`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveCampaignCategory(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/tags`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCampaignCategory(id: string, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/tags/${id}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAttachmentCount(estimateId: string, query: any = {}): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/attachment/${estimateId}/count`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCompanySettings(companyId: string): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/company/app/settings/${companyId}`, {});

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  // tire storage details for tech app
  getTireStorageDetails(data) {
    const query = {
      orderId: data.orderId,
      tireStorageId: data.tireStorageId,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/items/tire/storage/tech`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateTireStorage(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/items/tire/storage/update`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateTireStorageStatus(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/items/tire/storage/storageStatus`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * ==============================
   *          Helper Methods
   *  ==============================
   */

  private successHandler(res) {
    const d = res;
    try {
      if (d.error) {
        return Promise.resolve(d);
      }
      if (d.status) {
        return Promise.resolve(d.response);
      }
      return Promise.resolve(d);
    } catch (err) {
      console.error('Success Handler catch', err);
    }
  }

  private errorHandler(result: any) {
    try {
      if (!result?.error?.status) {
        if (result.status === 440 || result.status === 401) {
          this.helperService.logout();
        }

        return Promise.reject(result?.error?.error || result?.statusText);
      }
    } catch (err) {
      console.error('Error Handler catch', err);
    }
  }

  /**
   * ==============================
   *          Payments _ Payrix
   *  ==============================
   */

  getPayrixInfo(): Promise<getPayrixInfo> {
    return this.http
      .get<getPayrixInfo>(`${environment.api_base_url}/payrix/getInfoPreview`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  postPayrixInfo(data) {
    return this.http.post(`${environment.api_base_url}/payrix/connect`, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  putPayrixDisconnect() {
    return this.http
      .put<string>(`${environment.api_base_url}/payrix/disconnect`, {}, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPayrixPaymentMethods(): Promise<PaymentMethodsResponse[]> {
    return this.http
      .get<PaymentMethodsResponse[]>(`${environment.api_base_url}/payrix/paymentMethods`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  putOnlinePaymentMethods(body): Promise<Object> {
    return this.http
      .put(`${environment.api_base_url}/online/paymentMethods`, body, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getPayixInfoCustomer(data): Promise<any> {
    return this.http
      .get<string>(`${environment.api_base_url}/payrix/getInfo`, { headers: this.setHeaderToken(data) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPaymentCustomer(data, token): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payment/online`, this.helperService.encryptData(data), { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  //  payrix Payment Failed Count
  payrixPaymentFailedCount(estimateId: string, token: any, error: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/paymentFailed/${estimateId}`, { error }, { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getActivePaymentMethodsForPayrix(token) {
    return this.http
      .get(`${environment.api_base_url}/activepmPreview`, { headers: this.setHeaderToken(token) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTerminalListForOnlinePayment(merchantId): Promise<terminalRes> {
    return this.http
      .get<terminalRes>(`${environment.api_base_url}/payrix/terminals/list/${merchantId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createPaymentTerminal(payload): Promise<any> {
    return this.http
      .post<any>(`${environment.api_base_url}/payment/card/swipe`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  cancelPayment(id: string, type: string): Promise<any> {
    return this.http
      .put<any>(`${environment.api_base_url}/payment/unlock/${type}/${id}`, {}, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkPaymentStatus(id: string, tpye: string): Promise<any> {
    return this.http.get<any>(`${environment.api_base_url}/payment/checkStatus/${tpye}/${id}`, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this));
  }

  /**
   * ==============================
   *   Global Payment
   *  ==============================
   */

  getGlobalPaymentDetails(id: string, type: string, referenceId: string, isDebitRefund: boolean = false) {
    return this.http
      .get(`${environment.api_base_url}/globalPay/transactionDetail/${id}?type=${type}&referenceId=${referenceId}&isDebitRefund=${isDebitRefund}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  postGlobalPaymentInfo(data) {
    return this.http.post(`${environment.api_base_url}/globalPay/connect`, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getGlobalPaymentInfo() {
    return this.http.get(`${environment.api_base_url}/globalPay/getInfo`, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getGlobalPaymentInfoCustomer(data): Promise<any> {
    return this.http
      .get<string>(`${environment.api_base_url}/globalPay/getInfo`, { headers: this.setHeaderToken(data) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getGlobalPaymentInfoPreview() {
    return this.http.get(`${environment.api_base_url}/globalPay/getInfoPreview`, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getGlobalPaymentInfoCustomerPreview(data): Promise<any> {
    return this.http
      .get<string>(`${environment.api_base_url}/globalPay/getInfoPreview`, { headers: this.setHeaderToken(data) })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  putGlobalPaymentDisconnect(typeObject: any) {
    return this.http
      .put<string>(`${environment.api_base_url}/globalPay/disconnect`, typeObject, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createGlobalPayment(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payment/globalPay`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  globalPaymentcheckStatus(paymentId) {
    return this.http
      .get(`${environment.api_base_url}/globalPay/checkStatus/${paymentId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  lookupTransaction(orderId: string,transactionId:string,amount:number): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/payment/lookup/globalPay/${orderId}?transactionId=${transactionId}&amount=${amount}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  confirmLookupTransaction(data:any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/payment/lookupConfirm/globalPay`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  // CARD SWAP GP
  connectGlobalPaymentCardSwipe(payload): Promise<any> {
    return this.http
      .post<any>(`${environment.api_base_url}/globalPay/connect/cardSwipe`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  // add terminal
  globalPaymentAddEditTerminal(payload): Promise<any> {
    return this.http
      .post<any>(`${environment.api_base_url}/globalPay/save/terminal`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  // get terminal list
  globalPaymentGetTerminalList(isAuthDetails = false): Promise<any> {
    return this.http
      .get<any>(`${environment.api_base_url}/globalPay/get/terminals?isAuthDetails=${isAuthDetails}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteGlobalPaymentTerminal(id): Promise<any> {
    return this.http
      .delete<any>(`${environment.api_base_url}/globalPay/delete/terminal/${id}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  addPaymentCardSwip(payload) {
    return this.http
      .post<any>(`${environment.api_base_url}/payment/globalPay/cardSwipe`, payload, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  /**
   * ==============================
   *    Reports - End Of day report
   *  ==============================
   */

  getStats(tableConfig): Promise<labourSummaryStats> {
    return this.http
      .put<labourSummaryStats>(`${environment.api_base_url}/reports/dailyPayment/stats`, this.helperService.encryptData(tableConfig), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getReciveablesList(tableConfig): Promise<laborSummaryList> {
    return this.http
      .put<laborSummaryList>(`${environment.api_base_url}/reports/dailyPayment/receivables/list`, this.helperService.encryptData(tableConfig), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getRegularList(tableConfig): Promise<laborRegularList> {
    return this.http
      .put<laborRegularList>(`${environment.api_base_url}/reports/dailyPayment/list`, this.helperService.encryptData(tableConfig), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAudibleNotificationSettings() {
    const url = `${environment.api_base_url}/shop/notification`;
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  putAudibleNotificationSettings(body): Promise<Object> {
    return this.http.put(`${environment.api_base_url}/shop/notification`, body, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  //Assign Self Service
  assignServiceToSelf(orderId, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/assign/multiTechs/${orderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  assignUnassgnToSelf(orderId, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/assignAndUnassign/multiTechs/${orderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getVehicleFluidSpecifications(vehicleId: string, isFluidOnly = false) {
    return this.http
      .get(`${environment.api_base_url}/vehicles/fluid/details/${vehicleId}?isFluidOnly=${isFluidOnly}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  putAssignFee(data: any) {
    return this.http
      .put(`${environment.api_base_url}/company/taxFee`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  putAccountpayableSupplierTerm(data: any) {
    return this.http
      .put(`${environment.api_base_url}/company/accPayableTerms`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  saveReconcileDraw(reconcileId: any, data: any = {}): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/cashDrawer/balance/${reconcileId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  mergeVehicle(customerId: any, data: any = {}): Promise<any> {
    return this.http
      .post<any>(`${environment.api_base_url}/vehicles/merge/${customerId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  mergeCustomer(data): Promise<any> {
    return this.http.put<any>(`${environment.api_base_url}/customers/merge`, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  mergeQBOCustomer(data): Promise<any> {
    return this.http
      .put<any>(`${environment.api_base_url}/qbo/customerMerged`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  //User Center
  getUserCenter() {
    return this.http
      .get(`${environment.api_base_url}/userCenter`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  //View Print Sticker
  getCompanySelectedDetails(data) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/companies/appsettings/selected`, {
      settings: data,
    });
    return this.http
      .get(`${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateLocationSettings(data) {
    // const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/location/appsettings`);
    return this.http
      .put(`${environment.api_base_url}/shop/location/appsettings`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getUpdateLocationSettings(data) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/shop/location/appsettings`, {
      settings: data,
    });
    return this.http
      .get(`${url}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  //Credit Memo
  createMemo(data: any = {}): Promise<any> {
    return this.http.post<any>(`${environment.api_base_url}/creditMemo`, data, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  updateMemo(data: any = {}): Promise<any> {
    return this.http
      .put<any>(`${environment.api_base_url}/creditMemo/${data._id}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllCreditMemo(customerId, isApplyPayment = false, estimateId?: string) {
    let url = `${environment.api_base_url}/creditMemo/?customerId=${customerId}&isApplyPayment=${isApplyPayment}`;
    if (estimateId) {
      url += `&orderId=${estimateId}`;
    }
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCreditMemoList(query) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/creditMemo/`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCreditMemoById(memoId) {
    return this.http
      .get(`${environment.api_base_url}/creditMemo/${memoId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  //Apply credit payment
  createApplyMemo(data: any = {}): Promise<any> {
    return this.http
      .post<any>(`${environment.api_base_url}/creditMemo/apply`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllCreditIssued(estimateId) {
    return this.http
      .get(`${environment.api_base_url}/creditMemo/?estimateId=${estimateId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getAllCategories(categoryId, param = { isCannedService: false }): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/tags/items/${categoryId}`, param);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  checkInventoryForDuplicatePart(orderId: string, data: { orderSource: string }) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/estimate/checkForDuplicateParts/${orderId}`, data);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  reassignTech(serviceId: any, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimates/service/assignSelf/${serviceId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  removeStockTransferItem(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/transferOrder/remove/item`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // Supplier Report Drill Down
  getSupplierReportItemsPurchasedById(data: any): Promise<any> {
    const query: any = {
      limit: data.limit,
      page: data.page,
      sort: data.sortField || '',
      order: data.sortOrder || '',
      search: encodeURIComponent(data.searchQuery || ''),
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      _id: (data?.name?.length >= 1 ? '' : data._id) || '',
      type: data.type || '',
      name: data.name || [],
      drilldown: data.drilldown || true,
      shopIds: data.shopIds,
      paymentTerms: data.paymentTerms,
      orderingMethods: data.orderingMethods,
      orderingStatus: data.orderingStatus,
      dateFillter: data.dateFillter,
    };
    const url = `${environment.api_base_url}/suppliers/summaryList`;
    return this.http.put(url, query, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSupplierCors(data: any): Promise<any> {
    const query = {
      limit: data.limit,
      sort: data.sortField,
      page: data.page,
      order: data.sortOrder,
      search: data.search,
      itemType: data.itemType,
      name: data.name || [],
      orderingMethods: data.orderingMethods,
      orderingStatus: data.orderingStatus,
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      type: data.type || '',
      drilldown: data.drilldown || true,
      paymentTerms: data.paymentTerms,
      dateFillter: data.dateFillter,
      shopIds: data.shopIds,
    };

    const url = `${environment.api_base_url}/suppliers/adHocItemList`;

    return this.http.put(url, query, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  transferVehicles(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/vehicles/transfer/${data.vehicleId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTechDetails(orderId: string, data) {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/service-details/timesheet/${orderId}`, data);
    return this.http
      .get(url, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  techServiceAction(orderId: any, data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/order/service/timesheet/${orderId}`, data, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getInspectionNotesById(itemId?: string, data: { type: string } = { type: 'inspection' }) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/items/${itemId}`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getInspectionNotes(data?: {}) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/inspectionNotes`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTechAlerts(data) {
    return this.http
      .get(this.helperService.constructQueryParams(`${environment.api_base_url}/appLogs/alerts/unread`, data), {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCommissonDetails(data: any): Promise<any> {
    // http://localhost:3000/api/v1/employeeCommissionDetails/date/?page=1&limit=10&startDate=&endDate=
    const query = {
      limit: data.limit,
      page: data.page,
      startDate: data.startDate,
      endDate: data.endDate,
      isServiceWriter: data?.isServiceWriter || false,
    };

    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/employeeCommissionDetails/date`, query);

    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getLatestNotifications(data): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/appLogs/notifications?limit=${data.limit}&skip=${data.offset}&sort=${data.sortField}&order=${data.sortOrder}&priority=${data.priority}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getEarnedCommission(orderId: any, techId: any): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/serviceWriterCommissionDetails/${orderId}/${techId}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  reopenRepairOrder(orderId: string, data?: any): Promise<any> {
    return this.http
      .put<any>(`${environment.api_base_url}/estimates/reopen/order/${orderId}`, data || {}, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatePaymentStatus(data: {}, estimateId: ''): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/estimate/update/paymentStatus/${estimateId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }
  customerReturnRate(data: any): Promise<any> {
    const query = {
      startDate: data.startDate,
      endDate: data.endDate,
      isFleetCustomer: data.isFleetCustomer,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/dashboard/customerReturnRate`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  upcomingScheduleWork(data: any): Promise<any> {
    const query = {
      startDate: data.startDate,
      endDate: data.endDate,
      isFleetCustomer: data.isFleetCustomer,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/dashboard/upcomingScheduleWork`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  chartInvoiceAndReturnOrder(data: any): Promise<any> {
    const query = {
      startDate: data.startDate,
      endDate: data.endDate,
      activeView: data.activeView,
      isFleetCustomer: data.isFleetCustomer,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/dashboard/chartInvoiceAndReturnOrder`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  inspectionVehicleOrder(data: any): Promise<any> {
    const query = {
      startDate: data.startDate,
      endDate: data.endDate,
      isFleetCustomer: data.isFleetCustomer,
    };
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingCampaign/dashboard/inspectionVehicleOrder`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getSpeedDialCatalogueOptions(vehicleId: string): Promise<any> {
    return this.http
      .get(`${environment.api_base_url}/speeddial/catalogueOption/${vehicleId}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // getPricingCategories = (query?, itemType?) => {
  //   const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster?itemType=${itemType}`, query);

  //   return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  // };

  // getPricingSubCategories(id,itemType) {
  //   return this.http
  //     .get(`${environment.api_base_url}/codeMaster?categoryId=${id}&itemType=${itemType}`, {
  //       headers: this.getHeaders(),
  //     })
  //     .toPromise()
  //     .then(this.successHandler.bind(this))
  //     .catch(this.errorHandler.bind(this));
  // }

  // getPricingSubSubCategories(categoryId,subCategoryId,itemType) {
  //   return this.http
  //     .get(`${environment.api_base_url}/codeMaster?itemType=${itemType}&categoryId=${categoryId}&subCategoryId=${subCategoryId}`, {
  //       headers: this.getHeaders(),
  //     })
  //     .toPromise()
  //     .then(this.successHandler.bind(this))
  //     .catch(this.errorHandler.bind(this));
  // }

  // getCategoryList = (query?) => {
  //   const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster/list`, query);

  //   return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  // };

  createCategory(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/codeMaster/category`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCategory(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/codeMaster/category`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSubCategory(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/codeMaster/subCategory`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateSubSubCategory(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/codeMaster/subSubCategory`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deletingCategory(categoryId: string, itemType: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/codeMaster/category?categoryId=${categoryId}&itemType=${itemType}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  // codeMaster/subCategory?categoryId={{categoryId}}subCategoryId={{subCategoryId}}&itemType={{itemType}}
  deletingSubCategory(categoryId: string, subCategoryId: string, itemType: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/codeMaster/subCategory?categoryId=${categoryId}&subCategoryId=${subCategoryId}&itemType=${itemType}`, { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deletingSubSubCategory(categoryId: string, subCategoryId: string, subSubCategoryId: string, itemType: string): Promise<any> {
    return this.http
      .delete(`${environment.api_base_url}/codeMaster/subSubCategory?categoryId=${categoryId}&subCategoryId=${subCategoryId}&subSubCategoryId=${subSubCategoryId}&itemType=${itemType}`, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  deleteAndReplaceSalesGroup(data: any): Promise<any> {
    return this.http
      .put(`${environment.api_base_url}/categories/delete/replace/salesGroup`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createSubCategory(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/codeMaster/subCategory`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createSubSubCategory(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/codeMaster/subSubCategory`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  createBrand(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/codeMaster/brand`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getCatalogMatrixPricing(data: any) {
    return this.http
      .put(`${environment.api_base_url}/pricings/ItemList/${data.id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateCatalogMatrixPricing(data: any) {
    return this.http
      .put(`${environment.api_base_url}/pricings/updateItems/${data.id}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updatePartsTechBrand(data: any) {
    return this.http
      .put(`${environment.api_base_url}/partstech/brand/list`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  updateVehicleInRO(estimateId, data) {
    return this.http
      .put(`${environment.api_base_url}/estimates/vehicle/${estimateId}`, data, {
        headers: this.getHeaders(),
      })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getTireTypes = (query?, itemType?) => {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/codeMaster?itemType=${itemType}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  };

  googleAddressSearch({ search }) {
    const url = `${environment.api_base_url}/customers/google/address/search`;
    return this.http.put(url, { search }, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  googleAddressDetails({ placeId }) {
    const url = `${environment.api_base_url}/customers/google/address/details`;
    return this.http.put(url, { placeId }, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getInvStocksEstimateHistory(itemId: string, query?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/invStocks/estimateHistory/${itemId}`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getWarrantiesSearchByZCode(data: any): Promise<any> {
    return this.http
      .post(`${environment.api_base_url}/warranties/searchByZCode`, this.helperService.encryptData(data), { headers: this.headers })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  assignWarrantyToServiceItems(data: any, orderId: string) {
    return this.http
      .put(`${environment.api_base_url}/estimate/update/item/warranties/bulk/${orderId}`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  getWarrantyReportList(data: any) {
    return this.http
      .put(`${environment.api_base_url}/reports/warrantiesList`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  checkWarrantyBulkServiceItems(data: any) {
    return this.http
      .post(`${environment.api_base_url}/warranties/getByItemSpecBulk`, this.helperService.encryptData(data), { headers: this.getHeaders() })
      .toPromise()
      .then(this.successHandler.bind(this))
      .catch(this.errorHandler.bind(this));
  }

  buyMoreSMSCredits(){
    return this.http
    .post(`${environment.api_base_url}/marketingGroup/chargebeePurchaseSMS`,{}, { headers: this.getHeaders() })
    .toPromise()
    .then(this.successHandler.bind(this))
    .catch(this.errorHandler.bind(this));
  }

  getSMSCreditHistory(query?): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/marketingGroup/chargebeeSMS/smsCreditHistory`, query);
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

  getCustomReporingData(): Promise<any> {
    const url = this.helperService.constructQueryParams(`${environment.api_base_url}/reports/customReports`, {});
    return this.http.get(url, { headers: this.getHeaders() }).toPromise().then(this.successHandler.bind(this)).catch(this.errorHandler.bind(this));
  }

}
